import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import { ShowGlobalSnackbarMutationProps } from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import HoverTooltip, {
    TooltipPlacements,
} from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import {
    AddUpdateBestPracticeResponse,
    BestPracticeData,
    RemoveBestPracticeResponse,
} from '@deltek/specification-client/core/Models/bestPractice'
import { ContentItem as ContentItemModel } from '@deltek/specification-client/core/Models/contentItem'
import {
    DocumentTemplateView,
    DocumentTemplateViewItem,
} from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import {
    TextContentElement,
    TextContentElementInput,
} from '@deltek/specification-client/core/Models/textContentElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    compareBestPracticesMutation,
    getIsBP,
} from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { useDraggable } from '@dnd-kit/core'
import { CircularProgress, Radio, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Collapse from '@material-ui/core/Collapse'
import { Theme, makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import SustainabilityTopicMarker from '@material-ui/icons/EcoOutlined'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Skeleton from '@material-ui/lab/Skeleton/Skeleton'
import {
    AddUpdateBestPracticeMutation,
    IsBestPracticeOutOfSyncForUI,
    RemoveBestPracticeMutation,
    UpdateBestBestPracticeVersion,
} from 'api/graphQL/mutations/bestPractices.mutations'
import {
    SetProjectContentItemElements,
    UpdateBestPracticeElements,
} from 'api/graphQL/mutations/projectProductFamily.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import {
    GetElementNodesForOfficeMasterWorkspaceQuery,
    GetProjectElementNodesQuery,
    GetProjectNuggetQuery,
    GetProjectProductFamilyQuery,
    NuggetQuery,
    ProductFamilyQuery,
} from 'api/graphQL/queries/projectElements.queries'

import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { AddProjectContentItemInput } from '@deltek/specification-client/core/Models/projectProductProperty'
import {
    setIsLevelOfSpecificationAutomation,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import {
    resetOnFocusProperty,
    setArticleToFocus,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    newlyAddedParagraphText,
    promoteDemoteTriggered,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    isContentTeam,
    isOmWorkspaceUser,
    isWorkspaceEditable,
    promoteDemoteTemporaryParagraphData,
    reactiveWorkspaceContents,
    setIsAddOngoing,
    setIsAddProhibited,
    setIsCancelAddDialogOpen,
    setWorkspaceContentItems,
    setWorkspaceHasError,
    updatePromoteDemoteTemporaryParagraphData,
    workspaceContents,
    workspaceHasError,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import {
    GetNuggetResponse,
    GetProductFamilyResponse,
    GetProjectNuggetResponse,
    GetProjectProductFamilyResponse,
} from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import throttle from 'lodash.throttle'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { TrackChangeType } from 'models/changes'
import {
    GetOMProjectElementNodesInput,
    GetProjectElementNodesInput,
} from 'models/getProjectElementNodesInput'
import {
    GetOMProjectElementNodesResponseForWorkspace,
    GetProjectElementNodesResponse,
} from 'models/projectElementNode'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React, { Suspense, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import {
    canElementBeShown,
    isAllElementDeleted,
    useIsBestPracticeTagDisabled,
} from 'shared/utils/bestPracticeUtils'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    WORKSPACE_ENABLED_PTP_OPTIONS,
    WORKSPACE_NO_DROP_CURSOR_ICON,
} from 'shared/utils/workspaceUtils'
import {
    defaultColor,
    defaultOOFColor,
    errorColor,
    manufacturerSecondaryColor,
    manufacturerSecondaryColorOOFColor,
} from '../../../colorVars'
import { LoadingSkeleton } from '../LoadingSkeleton'
import OfficeMasterNote from '../OfficeMasterNote'
import { getUpdatedItemsArray } from '../WorkspaceContentArea'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import type { TextVariant } from '../constants'
import { AreaContext } from './AreaContext'
import Article, { getTitle } from './Article'
import {
    BestPracticeDataExtended,
    BestPracticeDataResponseExtended,
    BestPracticeSyncAction,
    Elements,
    SyncActionResult,
} from './BestPracticeSyncTypes'
import { DragDropAreaContext } from './DragDropAreaContext'
import DroppableParentChildren from './DroppableParentChildren'
import { DroppableSpot } from './DroppableSpot'
import { HoveredItemActions } from './HoveredItemActions'
import { TabsContext } from './TabsContext'
import {
    articleDefaultValue,
    defaultNewProperty,
    getNewlyAddedProperties,
    getPropertyDefaultValue,
    getPropertyEditingDetails,
    isSustainabilityTopicContent,
    isSustainabilityTopicContentInNuggetorPTP,
    removeDuplicateTemporaryProperty,
} from './addUtils'
import {
    createArticleInput,
    createNewPropertyStructure,
    createPillWithNewParaInput,
    createPropertyInput,
    setupArticleProperty,
    setupParagraphProperty,
} from './draggableItemUtils'
import { useNoteSpecifier } from './hooks'
import { useCustomTransition } from './hooks/useCustomTransition'
import {
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from './model'
import {
    ProductTypeInFamilyViewContentType,
    WorkspaceTabTitles,
    getActualKeyWithParent,
    getContentOwnerId,
    getContentOwnerIdforNewProperty,
    getContentOwnerType,
    getParentKey,
    getTopicContentItemsDetailForSustainabilityContent,
    getUniqueKey,
    isProductTypeIconShown,
} from './utils'

// lazy load large dependencies
const ConfirmationDialog = React.lazy(
    () => import('components/ConfirmationDialog/ConfirmationDialog')
)
const LazySpecifierNote = React.lazy(() => import('../SpecifierNote'))
const ContentItem = React.lazy(() => import('./ContentItem'))
const BestPracticesSetting = React.lazy(() =>
    import(
        '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
    ).then((module) => ({ default: module['BestPracticesSetting'] }))
)
const BestPracticesIcons = React.lazy(() =>
    import(
        '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
    ).then((module) => ({ default: module['BestPracticesIcons'] }))
)

export interface DraggableItemProps {
    template: NormalizedDocumentTemplateViewItem
    isDraggable: boolean
    parentType: string
    contentItemIndex: number
    addProperty?: (
        newProperty: NormalizedDocumentTemplateViewItem,
        indexPlacement?: number,
        articleIndex?: number
    ) => void
    updateChildInParent?: (
        updatedChild: NormalizedDocumentTemplateViewItem[]
    ) => void
    parent?: NormalizedDocumentTemplateViewItem
    isPropertyShared: boolean
    cancelAddProperty?: (value?: boolean) => void
    droppableId: string
    index: number
    setChildError?: (id: string) => void
    isLastChildOfParent?: boolean
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    selectedContent?: string
    isBasedFromManufacturerOption?: boolean
    setPromoteDemoteRequirements?: (input?: PromoteDemoteRequirements) => void
    setAreSharedElementsVisible: (value: boolean) => void
    disableArticleExpandInFullView?: boolean
    projectProductFamilyId?: string
    isAddProhibited?: boolean
    isAddOngoing?: boolean
    contentItemNodes?: Partial<ContentItemModel>[]
    dtv: Partial<DocumentTemplateView> | null
    isNewAddOnQueue?: boolean
    addQueueSib?: string[] | undefined
    elementIdOnEdit?: string | undefined
    tabSelected: number
    productTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
    hiddenSiblingsCount?: number
    useOrderInContentItem?: boolean
    isUnderProductGroupArticle?: boolean
    familyAddedProductTypes?: ProjectElementNode[]
    productTypeNodeInFamily?: Partial<ProjectElementNode>
    losOfflineBoltIcon?: React.ReactNode
    allContentItems?: Partial<NormalizedDocumentTemplateViewItem>[]
}

export interface DraggableItemStyleProps {
    localHovered: boolean
    textType?: string
    hasTitle: boolean
    isDraggable: boolean
    localDragStarted?: boolean
    hasChild?: boolean
    isDragging?: boolean
    isDraggingOver?: boolean
    isNewlyAddedArticle?: boolean
    isHidden?: boolean
    isPremiumPlus?: boolean
    areHiddenPropertiesVisible?: boolean
    hasError?: boolean
    isExpanded: boolean
    titleTextHeight: number
    isDroppable: boolean
    isAddedToProject?: boolean
    changeType?: TrackChangeType
    contentTeamFromStudioPage?: boolean
    isToBeHighlighted: boolean
    isSelectedItem?: boolean
    isContentTeamUser?: boolean
    isContentGroup?: boolean
    isOfficeMasterNote?: boolean
    isChangeToggle?: boolean
    disableArticleExpandInFullView?: boolean
    productTypeInFamilyViewContentType?: ProductTypeInFamilyViewContentType
    isProductGroupChild?: boolean
}

export const DRAGGABLE_ITEM_BORDER_RADIUS = 4

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        top: 0,
        width: 'max-content',
        bottom: 0,
        display: 'flex',
        zIndex: 1000,
        position: 'absolute',
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
        height: pxToRem(40),
        left: 0,
        right: 0,
        margin: 'auto',
        borderRadius: 5,
    },
    dialogWrapper: {
        position: 'absolute',
        zIndex: 1000,
        width: '100%',
        backgroundColor: defaultColor,
        opacity: 0.5,
        top: 0,
        bottom: 0,
        display: 'flex',
    },
    saperator: {
        backgroundColor: theme.palette.common.white,
        height: pxToRem(40),
        width: 1.2,
        borderTop: `5px solid ${defaultColor}`,
        borderBottom: `5px solid ${defaultColor}`,
    },
    buttonCancelOkay: {
        backgroundColor: defaultColor,
        borderRadius: 0,
        height: 40,
        color: 'white',
        '&:hover': {
            backgroundColor: defaultColor,
        },
    },
    buttonCancel: {
        marginLeft: theme.spacing(1),
    },
    elementOverlay: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'block',
        zIndex: 999,
        boxSizing: 'inherit',
        backgroundColor: defaultColor,
        opacity: 0.52,
        minHeight: 45,
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        margin: (props: DraggableItemStyleProps) => {
            if (props.localHovered && !props.hasTitle) {
                return theme.spacing(0)
            }
            if (
                props.localDragStarted &&
                !props.isDragging &&
                props.isDroppable &&
                !props.hasTitle
            ) {
                return theme.spacing(0)
            }

            return theme.spacing(0.125)
        },
    },
    articleLoadState: {
        width: '100',
        marginLeft: '25%',
    },
    container: {
        display: (props: DraggableItemStyleProps) =>
            !(props?.isHidden && !props?.areHiddenPropertiesVisible)
                ? 'flex'
                : 'none',
        height: (props: DraggableItemStyleProps) =>
            (props?.hasTitle &&
                (props?.isHidden ||
                    !props?.isAddedToProject ||
                    !props.contentTeamFromStudioPage)) ||
            props?.isContentGroup
                ? 'unset'
                : 'inherit',
        paddingBottom: (props: DraggableItemStyleProps) =>
            props.isSelectedItem ? 0 : theme.spacing(1),
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        position: 'relative',
        overflowAnchor: 'none',
    },
    contentItemContainer: {
        display: 'flex',
        boxSizing: 'inherit',
        boxShadow: (props: DraggableItemStyleProps) =>
            props.localHovered &&
            !(props.textType === 'Article' && props.isToBeHighlighted) &&
            !props.isOfficeMasterNote
                ? '0px 4px 4px rgba(0, 0, 0, 0.25)'
                : props.textType === 'Article' && props.isToBeHighlighted
                ? '0px 1px 4px rgba(0, 152, 214, 0.65)'
                : 'none',
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        margin: (props: DraggableItemStyleProps) => {
            if (
                props.localHovered &&
                !props.hasTitle &&
                !props.isOfficeMasterNote
            ) {
                return theme.spacing(0)
            }
            if (
                props.localDragStarted &&
                !props.isDragging &&
                props.isDroppable &&
                !props.hasTitle
            ) {
                return theme.spacing(0)
            }

            return theme.spacing(0)
        },
    },
    contentItemSelected: {
        display: 'flex',
        boxSizing: 'inherit',
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        margin: (props: DraggableItemStyleProps) => {
            if (props.localHovered && !props.hasTitle) {
                return theme.spacing(0)
            }
            if (
                props.localDragStarted &&
                !props.isDragging &&
                props.isDroppable &&
                !props.hasTitle
            ) {
                return theme.spacing(0)
            }

            return theme.spacing(0.125)
        },
    },
    contentItemContainerBorder: {
        border: (props: DraggableItemStyleProps) => {
            if (props.isOfficeMasterNote) {
                return 'none'
            }
            if (props.localHovered) {
                return '1px solid #DADADA'
            }
            if (
                props.localDragStarted &&
                props.isDroppable &&
                !props.isDragging &&
                props.isDraggable
            ) {
                return '1px dashed #13B5EA'
            }

            if (props.textType === 'Article') {
                return !props.isToBeHighlighted
                    ? '1px solid #DADADA'
                    : '1px solid rgba(0, 152, 214, 0.5)'
            }

            return '1px solid transparent'
        },
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    contentItemContainerBackground: {
        background: (props: DraggableItemStyleProps) =>
            props.localDragStarted &&
            !props.isDragging &&
            props.isDroppable &&
            props.isDraggable
                ? '#D9F3F9'
                : theme.palette.common.white,
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    contentItemContainerFamilyGroupProperty: {
        marginRight: (props: DraggableItemStyleProps) =>
            props.productTypeInFamilyViewContentType ===
            ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE
                ? 5
                : 0,
    },
    dragHandleContainerBackgroundColor: {
        backgroundColor: (props: DraggableItemStyleProps) =>
            props?.isHidden
                ? props.isPremiumPlus
                    ? manufacturerSecondaryColorOOFColor
                    : defaultOOFColor
                : props.hasError
                ? errorColor
                : props.isPremiumPlus
                ? manufacturerSecondaryColor
                : defaultColor,
    },
    contentItemDragHandleContainer: {
        display: (props: DraggableItemStyleProps) =>
            props.localHovered ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        borderTopLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        minWidth: (props: DraggableItemStyleProps) =>
            !props.isDraggable ? 24 : 'inherit',
    },
    contentItemContent: {
        width: '100%',
        position: 'relative',
        marginLeft: (props: DraggableItemStyleProps) =>
            props.localHovered && !props.isOfficeMasterNote
                ? 0
                : theme.spacing(3),
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    titleContainer: {
        height: (props: DraggableItemStyleProps) =>
            props.hasTitle ? props.titleTextHeight : 30,
        minHeight: (props: DraggableItemStyleProps) =>
            props.hasTitle ? 40 : 'inherit',
        paddingLeft: theme.spacing(1),
        borderBottom: (props: DraggableItemStyleProps) =>
            props.localHovered && props.isExpanded
                ? '1px solid #DADADA'
                : 'none',
    },
    titleContainerFlex: {
        display: 'flex',
    },
    collapseContainer: {
        paddingLeft: (props: DraggableItemStyleProps) =>
            props.textType === 'Article'
                ? theme.spacing(0.5)
                : theme.spacing(1.25),
        fontSize: pxToRem(14),
        borderTopRightRadius: (props: DraggableItemStyleProps) =>
            props.textType === 'Article' ? 0 : DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        paddingBottom: (props: DraggableItemStyleProps) =>
            props.textType === 'Article'
                ? theme.spacing(1.25)
                : theme.spacing(0),
    },
    borderRight: {
        borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    borderBottom: {
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomLeftRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    titleRightSideItem: {
        width: 'calc(100% - 169px)', // hover actions and expand/callapse icon width
    },
    titleRightSideItemFlex: {
        flex: 1,
    },
    titleLeftSideItem: { width: 28 },
    draggedItemErrorDarkBorder: {
        border: '1px solid #EB5757',
    },
    draggedItemErrorDarkBackgroundColor: {
        backgroundColor: '#EB5757',
    },
    draggedItemErrorLightBackgroundColor: {
        backgroundColor: '#FFDEDE',
        borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    draggingOkayDrop: {
        cursor: 'grabbing',
    },
    draggingErrorDrop: {
        cursor: `url(${WORKSPACE_NO_DROP_CURSOR_ICON}), auto`,
    },
    normalCursor: {
        cursor: 'pointer',
    },
    titleAdornment: {
        marginRight: theme.spacing(0.9),
        paddingTop: theme.spacing(0.2),
    },
    newPropertyLoading: {
        color: theme.palette.common.white,
    },
    BPIcons: {
        minWidth: theme.spacing(4),
        lineHeight: 0,
        fontSize: pxToRem(22),
        textAlign: 'center',
        color: '#10B091',
        position: 'absolute',
        right: theme.spacing(0),
        paddingTop: theme.spacing(0.45),
        zIndex: 1001,
    },
    BPIconsHover: {
        right: theme.spacing(17.2),
    },
    BPIconsHoverOnUserAddedTemplate: {
        right: theme.spacing(21),
    },
    susTopicMarker: {
        color: '#008000',
        cursor: 'pointer',
        position: 'absolute',
    },
    smmWorkspace: {
        marginLeft: theme.spacing(3.5),
    },
}))

const getItemStyle = (
    showInactivatedElements: boolean
): React.CSSProperties => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',

    display: showInactivatedElements ? 'inline-flex' : 'none',
    minHeight: 'inherit',
    width: '100%',

    // change background colour if dragging
    background: 'transparent',
    borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
})

//for user added content we will consider whole node in one
type HierarchicalContentElement = TextContentElementInput & {
    children?: HierarchicalContentElement[]
}

export const extractChildContentItemIds = (
    items: NormalizedDocumentTemplateViewItem[]
): string[] => {
    let ids = items?.flatMap((child) => child?.contentItem?.id)

    items?.forEach((child) => {
        if (child?.children?.length > 0) {
            ids = ids?.concat(extractChildContentItemIds(child?.children))
        }
    })

    return ids as string[]
}

export const ConditionalHoverTooltip: React.FC<{
    isShow: boolean
    children: React.ReactElement
}> = ({ isShow, children }) => {
    return isShow ? (
        <HoverTooltip text="Tab (→) or Shift + Tab (↝)" placements={['bottom']}>
            {children}
        </HoverTooltip>
    ) : (
        children
    )
}

/**
 * Recursive function to flatten the nested elements
 * If type is SelectionOptin make its child elements text into
 * a single text and update element text
 * @param elements
 * @returns Partial<TextContentElement>[]
 */
const getElements = (
    elements: Partial<TextContentElement>[]
): Partial<TextContentElement>[] => {
    return elements.reduce((result, eachElement) => {
        // check if nested and if nested, get the nested elements text
        // and make it the text value of this element
        if (
            eachElement.type === 'SelectionOption' &&
            eachElement.elements?.length
        ) {
            return [
                ...result,
                {
                    ...eachElement,
                    text: eachElement.elements
                        ?.reduce((textResult, element) => {
                            if (element.imperialUnits) {
                                return [
                                    ...textResult,
                                    element.imperialUnits ?? '',
                                ]
                            }
                            if (element.metricUnits) {
                                return [
                                    ...textResult,
                                    element.metricUnits ?? '',
                                ]
                            }
                            return [...textResult, element.text ?? '']
                        }, [] as string[])
                        .join(' '),
                },
            ]
        }
        return eachElement.type === 'ReplacementOption' &&
            eachElement.elements?.length! > 0
            ? [...result, eachElement]
            : [
                  ...result,
                  eachElement,
                  ...getElements(eachElement?.elements ?? []),
              ]
    }, [] as Partial<TextContentElement>[])
}

/**
 * Contains the dragging, collapsing, and the nesting of child
 * @param DraggableItemProps.template
 * @param DraggableItemProps.parentType - this is for the DND to identify the parent of current element
 * @param DraggableItemProps.isDraggable
 * @param DraggableItemProps.contentItemIndex
 */
export const DraggableItem = React.forwardRef<
    HTMLDivElement,
    DraggableItemProps
>(
    (
        {
            template,
            parentType,
            isDraggable = true,
            contentItemIndex,
            addProperty,
            parent,
            isPropertyShared,
            cancelAddProperty,
            droppableId,
            index,
            isLastChildOfParent,
            isDraftMode,
            contentTeamFromStudioPage,
            selectedTopic,
            selectedContent,
            setPromoteDemoteRequirements,
            setAreSharedElementsVisible,
            isBasedFromManufacturerOption,
            disableArticleExpandInFullView,
            projectProductFamilyId,
            isAddProhibited,
            isAddOngoing,
            contentItemNodes,
            dtv,
            isNewAddOnQueue,
            addQueueSib,
            elementIdOnEdit,
            tabSelected: tab,
            productTypeInFamilyViewContentType,
            hiddenSiblingsCount,
            useOrderInContentItem,
            isUnderProductGroupArticle,
            familyAddedProductTypes,
            productTypeNodeInFamily,
            losOfflineBoltIcon,
            allContentItems,
        },
        ref
    ) => {
        const { projectData } = React.useContext(ProjectPageContext)
        const { currentUserActiveFirm } = React.useContext(AppBarContext)
        const { dragStarted, removeProperty } = React.useContext(TabsContext)
        const { typeForFullView } = React.useContext(DragDropAreaContext)
        const newlyAddedParagraphTextValue = useReactiveVar(
            newlyAddedParagraphText
        )
        const isPromoteDemoteTriggered = useReactiveVar(promoteDemoteTriggered)
        const temporaryParagraphData = useReactiveVar(
            promoteDemoteTemporaryParagraphData
        )
        const isOMUser = useReactiveVar(isOmWorkspaceUser)
        const isContentTeamUser = useReactiveVar(isContentTeam)

        const { familyProjectMetadata } = useReactiveVar(
            workspaceSelectedElement
        )

        const [isBPMenuDisplayed, setIsBPMenuDisplayed] = React.useState<
            boolean
        >(false)

        const [
            popoverAnchor,
            setPopoverAnchor,
        ] = React.useState<null | Element>(null)
        const [autoExpandInFullView, setAutoExpandInFullView] = React.useState<
            boolean
        >(false)
        const [property, setProperty] = React.useState(template)
        const [isElementRemoved, setElementRemoved] = React.useState(false)

        // Maintain DraggableItem focus when clash detection dialog is open
        const [draggableItemFocus, setDraggableItemFocus] = React.useState(
            false
        )

        const propertyTextType = React.useMemo(() => {
            return property.contentItem?.textType || ''
        }, [property])

        const isPremiumPlus = useMemo(() => {
            const premiumPlusOptionGroups =
                property?.contentItem?.elements?.filter(
                    (item) =>
                        item.type === 'OptionGroup' &&
                        item.elements?.some(
                            (element) => !!element.isPremiumPlus
                        )
                ) ?? []
            return (
                isBasedFromManufacturerOption ||
                !!(property?.contentItem?.elements ?? [])[0]?.isPremiumPlus ||
                premiumPlusOptionGroups.length > 0
            )
        }, [property?.contentItem, isBasedFromManufacturerOption])

        // Set ProductType parent node when in family
        const currentProductTypeNodeInFamily = React.useMemo(() => {
            if (!!productTypeNodeInFamily) {
                // Inherit parent product type node
                return productTypeNodeInFamily
            } else if (
                !!productTypeInFamilyViewContentType &&
                isProductTypeIconShown(productTypeInFamilyViewContentType) &&
                !!familyAddedProductTypes
            ) {
                // Get product type node
                return familyAddedProductTypes.find(
                    (x: ProjectElementNode) => x.baseElementId === template.id
                )
            } else {
                return undefined
            }
        }, [
            productTypeInFamilyViewContentType,
            template,
            productTypeNodeInFamily,
            familyAddedProductTypes,
            isProductTypeIconShown,
        ])

        const { propertyToFocus, articleIdToFocus } = useReactiveVar(
            workspaceProperty
        )
        let isVirtualizedItem: boolean = false
        const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)

        const {
            isReviewer,
            currentUserInfo: { kpUserId },
            isSystemUser,
            isAdmin,
        } = useReactiveVar(userInfo)
        const {
            areNotesVisible,
            areHiddenPropertiesVisible,
            selectedElementId,
            selectedProjectNode,
            projectId,
            persistState,
            getPersistedState,
            updateExpandState,
            expandStateList,
            setManufacturerArticleId,
            handleIsContentItemReOrdered,
            handleAddNewProperty,
            handleChangeParagraphActionState,
            handleChangeParagraphItemId,
            selectedParagraphItemId,
            retrieveUpdatedElementChanges,
            handleHtmlParagraphPaste,
            manufacturerArticleIds,
        } = React.useContext(AreaContext)

        const isOMWorkspace = React.useMemo(
            () =>
                isOMUser &&
                projectId === currentUserActiveFirm?.bestPracticeProjectId,
            [projectId, isOMUser, currentUserActiveFirm]
        )

        const isProjectImported = reactiveWorkspaceContents()?.isProjectImported

        const isBPTagDisabledInput = {
            userInfo,
            isInProject: selectedProjectNode?.isInProject,
            contentTeamFromStudioPage,
            isProjectImported,
            isProjectOnly: selectedProjectNode?.isProjectOnly,
        }

        // checking if BP tags are disabled for editing
        const isBestPracticeEditDisabled = useIsBestPracticeTagDisabled({
            ...isBPTagDisabledInput,
        })

        // checking if BP tags are disabled for viewing
        const isBestPracticeViewDisabled = useIsBestPracticeTagDisabled({
            ...isBPTagDisabledInput,
            isViewOnly: true,
        })

        const titleTextRef = React.useRef<HTMLDivElement | null>(null)
        const toBeLazyLoaded = React.useMemo(() => {
            return (
                property.children?.length <= 0 && propertyTextType === 'Article'
            )
        }, [property])
        const customTabsEl = React.useMemo(
            () => document.getElementById(`droppable-area`),
            []
        )

        const { isDragging, over, active } = useDraggable({
            id: `draggable-${property.id}`,
        })
        const [articleLoading, setArticleLoading] = React.useState(false)
        const [articleLoaded, setArticleLoaded] = React.useState(false)
        const propertyLevel = React.useMemo(() => property.level, [property])
        const propertyId = React.useMemo(() => property.id, [property])
        const { ref: entryRef, inView, entry } = useInView({
            root: customTabsEl,
            rootMargin: '700px 0px 700px 0px',
            threshold: 0,
            triggerOnce: false,
        })
        const [_, startTransition] = useCustomTransition()

        const computedContentHeight = React.useMemo(
            () => entry?.target.clientHeight ?? 12,
            [entry]
        )

        const { setSelectedElementIdentifier } = React.useContext(
            ProjectPageContext
        )

        const isBestPractice: boolean = React.useMemo(() => {
            return template?.contentItem?.isBestPractice ?? false
        }, [template])

        const isGlobalBestPractice: boolean = React.useMemo(() => {
            return template?.contentItem?.isGlobalBestPractice ?? false
        }, [template])

        const isPreviouslyVirtualized = usePrevious(isVirtualizedItem)
        isVirtualizedItem = React.useMemo(() => {
            let isPropVirtualized =
                (propertyLevel < 4 && !propertyId.includes('temporary')) ??
                false
            if (
                articleIdToFocus &&
                (articleIdToFocus === property?.articleId ||
                    propertyToFocus ===
                        property?.contentItem?.elements?.[0]?.id)
            ) {
                isPropVirtualized =
                    !isPreviouslyVirtualized || (isPropVirtualized && false)
            }

            return isPropVirtualized
        }, [
            entryRef,
            propertyLevel,
            propertyId,
            property,
            articleIdToFocus,
            propertyToFocus,
        ])

        const {
            updateFamilyArticleData,
            updateProductArticleData,
            handleRetrieveUpdatedDTV,
            contentItemWorker,
            updateCategoryElementState,
            fetchSelectedProductFamilyWithTable,
        } = React.useContext(WorkspaceContentContext)
        const [
            localOptionDialogActionState,
            setLocalOptionDialogActionState,
        ] = React.useState<string>('')

        const showInactivatedElements = React.useMemo(() => {
            return !(template?.isHidden && !areHiddenPropertiesVisible)
        }, [template?.isHidden, areHiddenPropertiesVisible])

        const isWorkspaceInError = useReactiveVar(workspaceHasError)
        const isEditable = useReactiveVar(isWorkspaceEditable)

        const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
            workspaceToggles
        )
        const isMovable = React.useMemo(() => isEditable, [isEditable])
        const isTable = React.useMemo(() => property.type === 'Table', [
            property,
        ])
        const isTableSibling = React.useMemo(() => property?.isTableSibling, [
            property,
        ])

        const previousAddingState = usePrevious(isAddProhibited)
        const resetClick = React.useCallback(
            (value?: string) => {
                if (!template?.hasOptionGroup) return
                if (selectedParagraphItemId === value) {
                    handleChangeParagraphActionState?.('')
                    handleChangeParagraphItemId?.('')
                } else {
                    handleChangeParagraphItemId?.(value ?? '')
                    setLocalOptionDialogActionState('Reset')
                }
            },
            [
                selectedParagraphItemId,
                handleChangeParagraphItemId,
                handleChangeParagraphActionState,
                template,
            ]
        )
        const usePillsChange = React.useCallback(
            (dialogAction?: string) => {
                if (dialogAction === 'close') {
                    handleChangeParagraphActionState?.('')
                    handleChangeParagraphItemId?.('')
                } else {
                    handleChangeParagraphActionState?.(
                        localOptionDialogActionState
                    )
                }
            },
            [
                handleChangeParagraphItemId,
                handleChangeParagraphActionState,
                localOptionDialogActionState,
            ]
        )
        const isPillsDialogOpen: boolean = React.useMemo(() => {
            return selectedParagraphItemId === template?.itemId
        }, [selectedParagraphItemId, template])

        const [getElementsList] = useLazyQuery<
            | GetProjectElementNodesResponse
            | GetOMProjectElementNodesResponseForWorkspace,
            GetProjectElementNodesInput | GetOMProjectElementNodesInput
        >(
            isOMWorkspace
                ? GetElementNodesForOfficeMasterWorkspaceQuery
                : GetProjectElementNodesQuery,
            {
                fetchPolicy: 'no-cache',
                displayName: 'GetChildElementsList',
                onCompleted: (data) => {
                    if (data) {
                        startTransition(() =>
                            updateCategoryElementState(data, true)
                        )
                        startTransition(() => {
                            const [key1, key2] =
                                selectedProjectNode?.elementType ===
                                'PRODUCTFAMILY'
                                    ? ['id', 'treePath']
                                    : ['parentNodeId', 'parentTreePath']
                            setSelectedElementIdentifier({
                                elementType: 'PRODUCTFAMILY',
                                id: selectedProjectNode?.[
                                    key1 as keyof typeof selectedProjectNode
                                ]! as string,
                                treePath: selectedProjectNode?.[
                                    key2 as keyof typeof selectedProjectNode
                                ]! as string,
                                baseElementId: selectedProjectNode?.[
                                    key1 as keyof typeof selectedProjectNode
                                ]! as string,
                            })
                        }, 100)
                    }
                },
            }
        )

        const [updateContentItemElement] = useMutation(
            SetProjectContentItemElements(6),
            {
                fetchPolicy: 'no-cache',
                onCompleted: React.useCallback(
                    (data) => {
                        setIsAddProhibited(false)

                        if (
                            template?.contentItem?.textType === 'Article' &&
                            isElementRemoved
                        ) {
                            startTransition(() => {
                                const input = isOMWorkspace
                                    ? {
                                          projectId,
                                          parentNodeId:
                                              selectedProjectNode?.elementType ===
                                              'PRODUCTFAMILY'
                                                  ? selectedProjectNode?.id!
                                                  : selectedProjectNode?.parentNodeId!,
                                      }
                                    : {
                                          projectId: projectId,
                                          limit: 1000,
                                          skip: 0,
                                          onlyElementsAssignedToProject: false,
                                          projectElementType: Object.keys(
                                              ProjectElementType
                                          ) as ProjectElementType[],
                                          parentNodeId:
                                              selectedProjectNode?.elementType ===
                                              'PRODUCTFAMILY'
                                                  ? selectedProjectNode?.id!
                                                  : selectedProjectNode?.parentNodeId!,
                                          isProductFamilyGroupView: false,
                                          isFromDasboardView: true,
                                          isFromAEWorkSpace: !contentTeamFromStudioPage,
                                      }

                                getElementsList({
                                    variables: {
                                        input,
                                    },
                                })
                            })
                        }

                        setElementRemoved(false)
                        showGlobalSnackbar({
                            variables: {
                                message: 'Changes Saved Successfully',
                                variant: 'success',
                                id: `ContentItemElementText-DeleteElementSuccess`,
                            },
                        })

                        //deleting the old dtvcontentitem to handle changes in ids
                        startTransition(() => {
                            setWorkspaceContentItems(
                                getUpdatedItemsArray(
                                    contentItemNodes ?? [],
                                    data?.setProjectContentItemElements
                                        ?.contentItemNodes ?? [],
                                    true,
                                    false
                                )
                            )
                        })
                        startTransition(() => {
                            handleRetrieveUpdatedDTV(property?.partType)
                            retrieveUpdatedElementChanges()
                        })
                    },
                    [
                        contentItemNodes,
                        handleRetrieveUpdatedDTV,
                        retrieveUpdatedElementChanges,
                        dtv,
                        isElementRemoved,
                        contentTeamFromStudioPage,
                    ]
                ),
                onError: (error) => {
                    if (error.networkError) {
                        showGlobalSnackbar({
                            variables: {
                                message:
                                    'There was a problem saving your changes. Please copy your recent edits and reconnect to the network.',
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                    } else if (error.message.includes('character')) {
                        showGlobalSnackbar({
                            variables: {
                                message: `There was a problem saving your changes. Please remove "|" to save your changes.`,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                    } else {
                        showGlobalSnackbar({
                            variables: {
                                message: `There was a problem saving your changes.`,
                                variant: 'error',
                                id:
                                    'WorkspacePage_SetProjectContentItemElementError',
                            },
                        })
                    }
                },
            }
        )

        const [getProductFamily] = useLazyQuery<GetProductFamilyResponse>(
            ProductFamilyQuery(6),
            {
                fetchPolicy: 'no-cache',
                onCompleted: (data: GetProductFamilyResponse) => {
                    startTransition(() => {
                        setArticleLoading(false)
                        updateFamilyArticleData?.(data, false, false)
                        setArticleLoaded(true)
                    })
                },
                onError: () => {
                    setArticleLoading(false)
                    updateExpandState?.(template.contentItem?.id ?? '')
                },
            }
        )

        const [getProjectProductFamily] = useLazyQuery<
            GetProjectProductFamilyResponse
        >(GetProjectProductFamilyQuery(6), {
            fetchPolicy: 'no-cache',
            onCompleted: (data: GetProjectProductFamilyResponse) => {
                startTransition(async () => {
                    const updatedContentItems = getUpdatedItemsArray(
                        contentItemNodes ?? [],
                        data?.getProjectProductFamily?.contentItemNodes ?? [],
                        true,
                        false
                    )

                    setWorkspaceContentItems(updatedContentItems)
                    setArticleLoading(false)
                    setArticleLoaded(true)
                })
            },
            onError: () => {
                setArticleLoading(false)
                setArticleLoaded(true)
                updateExpandState?.(template.contentItem?.id ?? '')
            },
        })

        const [
            currentSpecifierNoteId,
            setCurrentSpecifierNoteId,
        ] = React.useState<string>('')

        const [getSelectedNugget] = useLazyQuery<GetProjectNuggetResponse>(
            GetProjectNuggetQuery(6),
            {
                fetchPolicy: 'no-cache',
                displayName: 'GetProjectNugget',
                onCompleted: (data) => {
                    startTransition(() => {
                        setArticleLoading(false)
                        setArticleLoaded(true)
                        updateProductArticleData?.(data, true)
                    })
                },
                onError: () => {
                    setArticleLoading(false)
                    updateExpandState?.(template.contentItem?.id ?? '')
                },
            }
        )

        const [getNugget] = useLazyQuery<GetNuggetResponse>(NuggetQuery(6), {
            fetchPolicy: 'no-cache',
            displayName: 'Nugget',
            onCompleted: React.useCallback(
                (data: GetNuggetResponse) => {
                    startTransition(() => {
                        setArticleLoading(false)
                        setArticleLoaded(true)
                        updateProductArticleData?.(data)
                    })
                },
                [updateProductArticleData]
            ),
            onError: (error) => {
                setArticleLoading(false)
                updateExpandState?.(template.contentItem?.id ?? '')
            },
        })

        const { showGlobalSnackbar } = useGlobalSnackbar()

        const hasTitle = React.useMemo(() => {
            return (
                template.contentItem?.textType === 'Article' ||
                template.contentItem?.textType === 'Part' ||
                (template.contentItem?.textType === 'Paragraph' &&
                    productTypeInFamilyViewContentType ===
                        ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE)
            )
        }, [template, productTypeInFamilyViewContentType])

        const isAEUserProjectWorkspace = React.useMemo(
            () =>
                !contentTeamFromStudioPage &&
                processEnv.REACT_APP_EnableVersionControl === '1' &&
                isOMUser,
            [contentTeamFromStudioPage, isOMUser, processEnv]
        )

        const expanded = React.useMemo(() => {
            if (hasTitle && expandStateList) {
                return expandStateList.includes(property.contentItem?.id ?? '')
            }
            return true
        }, [expandStateList, property, hasTitle])

        const isOver = React.useMemo(() => !!over, [over])

        const isDroppable: boolean = React.useMemo(() => {
            const acceptedType =
                hasTitle && droppableId?.toLowerCase().includes('dragdroparea')
                    ? 'article'
                    : 'content'

            const isNeglected = !!active?.data?.current?.sortable?.neglect?.find(
                (child: string) => child === property.id
            )

            const isNestingAllowed = !active?.data?.current?.isUserAdded
                ? droppableId === active?.data?.current?.sortable?.containerId
                : true

            return (
                !!active &&
                active?.data?.current?.sortable?.type?.toLowerCase() ===
                    acceptedType &&
                !isNeglected &&
                isNestingAllowed &&
                !active?.data?.current?.disabled
            )
        }, [active, hasTitle, droppableId, property])

        const isDragError: boolean = React.useMemo(() => {
            if (hasTitle && expanded) return false
            if (!active) return false
            if (!isDraggable) return false

            const acceptedType = active?.data?.current?.sortable?.type

            return (
                over?.data?.current?.sortable?.type?.toLowerCase() !==
                    acceptedType &&
                (active?.id as string)?.includes(property?.id)
            )
        }, [over, active, property, isDraggable, hasTitle, expanded])

        const isAddLoading = React.useMemo(() => {
            return isAddOngoing && property?.id?.includes('temporary')
        }, [isAddOngoing, property])

        const articleTitle = React.useMemo(() => {
            return (
                getTitle(
                    property,
                    property.partType ?? '',
                    contentItemIndex,
                    contentTeamFromStudioPage,
                    productTypeInFamilyViewContentType ===
                        ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE,
                    isAEUserProjectWorkspace
                ) ?? ''
            )
        }, [
            property,
            contentItemIndex,
            contentTeamFromStudioPage,
            productTypeInFamilyViewContentType,
            isAEUserProjectWorkspace,
        ])

        const [localHovered, setLocalHovered] = React.useState<boolean>(false)
        const [localDragStarted, setLocalDragStarted] = React.useState<boolean>(
            false
        )

        const isOMDeactivatedTooltipVisible = React.useMemo(
            () =>
                !contentTeamFromStudioPage &&
                template?.isDeactivated &&
                localHovered,
            [template, localHovered, contentTeamFromStudioPage]
        )

        const notes = React.useMemo(() => {
            const allElementDeleted = isAllElementDeleted(template)

            if (!isTrackChangeToggleOn && allElementDeleted)
                return [] as string[]

            return template.contentItem?.comments
        }, [template, isTrackChangeToggleOn])

        const itemId = React.useMemo(() => `item${template.id}`, [template])
        const parentId = React.useMemo(() => `item${template.parentId}`, [
            template,
        ])

        const [warningDialogVariant, setWarningDialogVariant] = React.useState<
            'removeproperty' | 'canceladd' | undefined
        >(undefined)
        const [isCancelAddCancelled, setIsCancelAddCancelled] = React.useState<
            boolean | undefined
        >()

        const [
            pendingNewParagraphVariant,
            setPendingNewParagraphVariant,
        ] = React.useState<'keyless' | 'keyed' | undefined>()

        const [isSyncActionLoading, setIsSyncActionLoading] = React.useState(
            false
        )
        const [syncActionType, setSyncActionType] = React.useState<
            SyncActionResult
        >({
            action: BestPracticeSyncAction.Null,
            currentVersionId: '',
            isUserAddedContentBestPractice: false,
            latestVersionId: '',
            message: '',
        })

        const propertyChildren = React.useMemo(() => {
            return (
                property?.children?.filter((child) => {
                    return areSharedVisible ? true : !child.isSharedProperty
                }) ?? []
            )
        }, [areSharedVisible, property])

        const handleDialogClose = () => {
            if (warningDialogVariant === 'canceladd') {
                setIsCancelAddCancelled(true)
            }
            setWarningDialogVariant(undefined)
            setIsCancelAddDialogOpen(false)
        }

        const onClickRemove = React.useCallback(() => {
            if (
                isEditable &&
                !property.addingState &&
                !property.isHidden &&
                !property.isDeactivated &&
                property?.isUserAdded
            ) {
                setWarningDialogVariant('removeproperty')
            }
        }, [isEditable, property])

        const onRemoveFromProject = React.useCallback(
            (_: React.MouseEvent<Element, MouseEvent>) => {
                setWarningDialogVariant(undefined)
                setIsCancelAddDialogOpen(false)
                if (warningDialogVariant === 'removeproperty') {
                    const isNestedContentItem = parent?.type === 'ContentItem'
                    const isActuallyPTP =
                        isNestedContentItem &&
                        property?.propertyRefId &&
                        property?.propertyRefId !== ''

                    removeProperty?.(
                        {
                            projectId:
                                property?.documentTemplateView?.projectId,
                            documentTemplateViewId:
                                property?.documentTemplateView?.id,
                            documentTemplateViewItemId:
                                property?.documentTemplateViewItem?.itemId,
                            contentItemOwnerType: isNestedContentItem
                                ? !isActuallyPTP
                                    ? parent?.parentType
                                    : 'ProductTypeProperty'
                                : property?.parentType ?? '',
                            contentItemOwnerId: hasTitle
                                ? (property?.itemId as string)
                                : getContentOwnerId(
                                      property as NormalizedChildDocumentTemplateViewItem
                                  ),
                            contentItemId: property?.contentItem?.id ?? '',
                            partType: property?.partType,
                            projectProductFamilyId: projectProductFamilyId,
                        },
                        hasTitle
                    )
                } else if (warningDialogVariant === 'canceladd') {
                    cancelAddProperty?.()
                    if (isCancelAddCancelled) {
                        setIsCancelAddCancelled(undefined)
                    }
                }
            },
            [
                warningDialogVariant,
                property,
                parent,
                removeProperty,
                hasTitle,
                projectProductFamilyId,
                cancelAddProperty,
                isCancelAddCancelled,
                kpUserId,
                propertyChildren,
            ]
        )

        const getParentDetails = React.useCallback(() => {
            return {
                contentItemParentId: property?.parentId ?? parent?.id ?? '',
                parentKey: parent?.parentKey ?? '',
                parentType:
                    property?.parentId === parent?.id
                        ? parent?.type ?? ''
                        : property?.type ?? '',
            }
        }, [parent, property])

        const editingInput = getPropertyEditingDetails({
            property: template,
            isForArticle: false,
            nodeId: selectedTopic
                ? selectedTopic?.projectElementNodeId ?? ''
                : selectedProjectNode?.id ?? '',
            ...getParentDetails(),
            selectedTopic,
        })

        const deleteFamilyContent = React.useCallback(() => {
            if (!isDraftMode) {
                return false
            }

            setElementRemoved(true)

            const updatedElements = template?.contentItem?.elements?.map(
                (elementIterator) => {
                    return {
                        id: elementIterator.id,
                        imperialUnits: elementIterator.imperialUnits,
                        metricUnits: elementIterator.metricUnits,
                        optionItemState: elementIterator.optionItemState,
                        text: '',
                        type: elementIterator.type,
                        placeholderText: elementIterator.placeholderText,
                        changeType: 'DELETED',
                        authoredUserId: elementIterator.authoredUserId,
                        elements: elementIterator?.elements?.map((element) => {
                            return {
                                id: element.id,
                                imperialUnits: element.imperialUnits,
                                metricUnits: element.metricUnits,
                                optionItemState: element.optionItemState,
                                text: '',
                                type: element.type,
                                placeholderText: null,
                                changeType: null,
                                authoredUserId: element.authoredUserId,
                            }
                        }),
                    }
                }
            ) as Partial<TextContentElementInput>[]

            updateContentItemElement({
                variables: {
                    input: {
                        ...editingInput,
                        isDeletingBaseContent: !template?.isUserAdded,
                        elements: updatedElements,
                        isDeletingWholeParagraph: true,
                        topicContentItemsDetail: getTopicContentItemsDetailForSustainabilityContent(
                            template,
                            dtv?.items
                        ),
                    },
                    partType: String(editingInput.partType.toLocaleUpperCase()),
                    projectId,
                    rootArticleContentItemIds: [template.articleId ?? ''],
                    productFamilyId: selectedTopic
                        ? selectedTopic?.ppfId
                        : projectProductFamilyId,
                },
            })
        }, [
            updateContentItemElement,
            selectedProjectNode,
            getParentDetails,
            tab,
            projectProductFamilyId,
            projectId,
            selectedTopic,
            dtv,
        ])

        const confirmationDialogContent = React.useMemo(() => {
            return warningDialogVariant === 'removeproperty' ? (
                <>This action will remove this property from your project.</>
            ) : (
                <>
                    This action will cancel the proposed addition to your
                    project.
                </>
            )
        }, [warningDialogVariant])

        const handleChildUpdatesForParents = React.useCallback(
            (newChildren: NormalizedDocumentTemplateViewItem[]) => {
                const latestChildrenContent = contentItemNodes?.filter((id) =>
                    newChildren?.find((x) => x.contentItem?.id === id.id)
                )

                // removes duplicate new properties if there are any
                newChildren = removeDuplicateTemporaryProperty(newChildren)

                const dsfs = newChildren?.map((updatedChild) => {
                    if (updatedChild?.id !== 'temporaryTemplateId') {
                        const childUpdatedContent = latestChildrenContent?.find(
                            (child) => child.id === updatedChild.contentItem?.id
                        )
                        return {
                            ...updatedChild,
                            contentItem: childUpdatedContent,
                        }
                    } else {
                        return updatedChild
                    }
                }) as NormalizedDocumentTemplateViewItem[]

                const timeout = setTimeout(() => {
                    setProperty((past) => ({
                        ...past,
                        children: isNewAddOnQueue ? dsfs : newChildren,
                    }))
                    clearTimeout(timeout)
                }, 100)

                if (isWorkspaceInError) {
                    setWorkspaceHasError?.(false)
                }
            },
            [isWorkspaceInError, contentItemNodes, isNewAddOnQueue]
        )

        const handleAddProperty = React.useCallback(
            (temporaryParagraphText?: string) => {
                const newProperty: Partial<NormalizedChildDocumentTemplateViewItem> = {
                    ...defaultNewProperty,
                    contentItem: {
                        ...defaultNewProperty.contentItem,
                        elements: [
                            {
                                ...defaultNewProperty.contentItem
                                    ?.elements?.[0],
                                text: hasTitle
                                    ? articleDefaultValue
                                    : temporaryParagraphText ||
                                      getPropertyDefaultValue(),
                            },
                        ],
                    },
                }

                if (!hasTitle) {
                    newProperty.newProperties = getNewlyAddedProperties(
                        parent!,
                        isPropertyShared,
                        property
                    )
                    // For sustainability topic content, new added paragaraph should have the ability to demote at level 1
                    // as Sustainability content doesn't have parent at level 1, we set it to 0
                    const currentLevel =
                        isSustainabilityTopicContent(property) &&
                        property?.level === 1
                            ? 0
                            : parent?.level
                    newProperty.level = (currentLevel as number) + 1
                    newProperty.order = !!isSustainabilityTopicContentInNuggetorPTP(
                        property
                    )
                        ? (property?.order as number) +
                          property?.children?.length +
                          1
                        : (property?.order as number) + 1
                    newProperty.paragraphOrder =
                        property?.type === 'Table' || property?.isTableSibling
                            ? (property?.order as number) + 1
                            : isSustainabilityTopicContentInNuggetorPTP(
                                  property
                              )
                            ? newProperty.order + 1
                            : (property?.level as number) <= 2
                            ? (property?.paragraphOrder as number) + 1
                            : undefined

                    newProperty.addingType = 'keyless'
                    addProperty?.(
                        newProperty as NormalizedDocumentTemplateViewItem,
                        selectedTopic
                            ? (property?.order as number) + 1
                            : undefined,
                        selectedTopic
                            ? (contentItemIndex as number) + 1
                            : undefined
                    )
                    setPendingNewParagraphVariant('keyless')
                } else {
                    newProperty.contentItem = {
                        ...newProperty.contentItem,
                        textType: 'Article',
                    }
                    addProperty?.(
                        {
                            ...newProperty,
                        } as NormalizedDocumentTemplateViewItem,
                        (property?.order as number) + 1,
                        (contentItemIndex as number) + 1
                    )
                }
            },
            [
                parent,
                property,
                addProperty,
                contentItemIndex,
                hasTitle,
                isPropertyShared,
            ]
        )
        const getInitialPropertiesForBPConfigAdd = (
            property: NormalizedDocumentTemplateViewItem,
            parent?: NormalizedDocumentTemplateViewItem,
            temporaryParagraphText?: string
        ) => {
            const hasTitleT = !parent
            const newProperty = createNewPropertyStructure(
                hasTitleT,
                temporaryParagraphText
            )

            if (!hasTitleT) {
                setupParagraphProperty(
                    newProperty,
                    property,
                    parent!,
                    isPropertyShared
                )
                return newProperty
            } else {
                setupArticleProperty(newProperty)
                return newProperty
            }
        }

        const getSiblings = React.useCallback(
            (
                myContentItemId: string,
                includeSelf?: boolean,
                includeOrderAndHidden?: boolean
            ) => {
                // consider only latest content and available in updated dtv.
                let siblings = parent?.children?.filter((child) => {
                    if (!includeSelf) {
                        if (child.contentItem?.id === myContentItemId) {
                            return false
                        }
                    }
                    const allContentDeleted = child?.contentItem?.elements?.every(
                        (e) =>
                            e.changeType?.toLocaleLowerCase() ===
                            'DELETED'.toLocaleLowerCase()
                    )
                    const { dtv } = workspaceContents()
                    const itemNotFound =
                        allContentDeleted &&
                        !!!dtv?.items?.find(
                            (x) =>
                                x?.itemId &&
                                child?.itemId &&
                                x?.itemId === child?.itemId
                        )

                    if (itemNotFound) {
                        return false
                    }

                    return true
                })

                if (includeOrderAndHidden) {
                    return siblings?.map(
                        (child) =>
                            child.id +
                            '|' +
                            child.order +
                            '|' +
                            (child.isHidden ? 'true' : 'false')
                    ) as string[]
                } else {
                    return siblings?.map(
                        (child) => child.contentItem?.id
                    ) as string[]
                }
            },
            [parent]
        )

        const saveNewlyAddedProperty = React.useCallback(
            (
                newValue: string,
                indexOfPillForNewPt?: number,
                typeOfPillForNewPt?: TextVariant,
                isPillWithNewPara?: boolean
            ) => {
                if (isCancelAddCancelled) {
                    setIsCancelAddCancelled(undefined)
                }
                const defaultProperties = {
                    projectId: projectId,
                    projectProductFamilyId: projectProductFamilyId ?? '',
                    elements: [
                        {
                            text: newValue ? newValue : '',
                        } as TextContentElementInput,
                    ],
                }

                if (property.addingState && newValue !== '') {
                    if (!hasTitle) {
                        const uniqueKey = getUniqueKey(newValue)
                        let parentKey = ''

                        // Check if parent of the current content is user-added, level > 1, and has no parentKey
                        // Get the parent key from the contentItem if available
                        if (
                            parent?.isUserAdded &&
                            parent?.level > 1 &&
                            !parent?.parentKey
                        ) {
                            const parentElementText =
                                parent.contentItem?.elements?.[0]?.text
                            parentKey = parentElementText
                                ? getParentKey(parentElementText)
                                : ''
                        } else {
                            parentKey = parent?.parentKey
                                ? getParentKey(parent.parentKey)
                                : ''
                        }

                        const actualKeyWithParent = getActualKeyWithParent(
                            uniqueKey as string,
                            parentKey
                        )

                        const contentItemParentType = selectedTopic
                            ? 'None'
                            : template?.newProperties?.contentItemParentType ===
                              'ContentItem'
                            ? template?.newProperties?.contentItemParentType
                            : parent?.parentType

                        const contentItemOwnerType = getContentOwnerType(
                            parent as NormalizedChildDocumentTemplateViewItem,
                            template as NormalizedDocumentTemplateViewItem
                        )

                        const contentItemOwnerId = getContentOwnerIdforNewProperty(
                            uniqueKey as string,
                            parent as NormalizedChildDocumentTemplateViewItem,
                            template as NormalizedDocumentTemplateViewItem
                        )

                        handleAddNewProperty?.(
                            {
                                ...property.newProperties,
                                ...defaultProperties,
                                index: property?.order,
                                contentItemOwnerType,
                                contentItemParentType,
                                contentItemOwnerId,
                                propertyKey: actualKeyWithParent,
                            },
                            [parent?.articleId ?? ''],
                            getSiblings(property?.contentItem?.id as string),
                            parent?.documentTemplateViewItem?.isProductGroup,
                            indexOfPillForNewPt,
                            typeOfPillForNewPt,
                            undefined,
                            currentProductTypeNodeInFamily
                        )

                        if (uniqueKey) {
                            setPendingNewParagraphVariant('keyed')
                        }
                    } else {
                        const isPTGroupArticleNugget =
                            parent?.partType == 'Part' && parent.isProduct

                        const input: Partial<AddProjectContentItemInput> = {
                            ...defaultProperties,
                            contentItemOwnerType: 'Nugget',
                            contentItemParentId: property.parentId as string,
                            contentItemParentType: 'None',
                            index: property?.order as number,
                            partType: property.partType,
                            textType: 'Article',
                            subElements: [{ text: 'Insert your Paragraph.' }],
                            productTypePropertyType: undefined,
                        }

                        if (!isPTGroupArticleNugget) {
                            input.contentItemOwnerType = 'ProductTypeProperty'
                            input.propertyKey = newValue
                            input.productTypePropertyType = 'Shared'
                        }

                        handleAddNewProperty?.(
                            input,
                            [parent?.articleId ?? ''],
                            undefined,
                            undefined,
                            indexOfPillForNewPt,
                            typeOfPillForNewPt,
                            isPillWithNewPara,
                            currentProductTypeNodeInFamily
                        )
                    }
                } else if (isPillWithNewPara) {
                    if (!hasTitle) {
                        const uniqueKey = getUniqueKey(newValue)
                        const parentKey = getParentKey(
                            parent?.parentKey as string
                        )
                        const actualKeyWithParent = getActualKeyWithParent(
                            uniqueKey as string,
                            parentKey
                        )

                        handleAddNewProperty?.(
                            {
                                ...property.newProperties,
                                ...defaultProperties,
                                contentItemOwnerId: !uniqueKey
                                    ? parent?.type === 'ContentItem'
                                        ? getContentOwnerId(
                                              parent as NormalizedChildDocumentTemplateViewItem
                                          )
                                        : parent?.id
                                    : undefined,
                                propertyKey: actualKeyWithParent,
                            },
                            [parent?.articleId ?? ''],
                            getSiblings(property?.contentItem?.id as string),
                            parent?.documentTemplateViewItem?.isProductGroup,
                            indexOfPillForNewPt,
                            typeOfPillForNewPt,
                            isPillWithNewPara,
                            currentProductTypeNodeInFamily
                        )

                        if (uniqueKey) {
                            setPendingNewParagraphVariant('keyed')
                        }
                    }
                }

                setIsAddOngoing(true)
            },
            [
                handleAddNewProperty,
                projectId,
                property,
                isCancelAddCancelled,
                parent,
                projectProductFamilyId,
                hasTitle,
                getSiblings,
                selectedTopic,
                template,
            ]
        )
        const getPropertyForSettingBestPracticesConfig = (
            newValue: string,
            newProperties: any,
            template: NormalizedDocumentTemplateViewItem,
            parent: NormalizedDocumentTemplateViewItem,
            indexOfPillForNewPt?: number,
            typeOfPillForNewPt?: TextVariant,
            isPillWithNewPara?: boolean
        ) => {
            const hasTitleT = !parent
            const isValidInput = property && newValue !== ''
            const pid = projectId || ''

            if (isValidInput) {
                return hasTitleT
                    ? createArticleInput(
                          newValue,
                          property,
                          parent,
                          pid,
                          projectProductFamilyId
                      )
                    : createPropertyInput(
                          newValue,
                          property,
                          parent,
                          template,
                          newProperties,
                          selectedTopic,
                          pid,
                          projectProductFamilyId,
                          true // useNewContentOwnerType
                      )
            }

            if (isPillWithNewPara && !hasTitleT) {
                return createPillWithNewParaInput(
                    newValue,
                    property,
                    parent,
                    pid,
                    projectProductFamilyId
                )
            }

            return
        }

        const handleSaveNewArticle = React.useCallback(
            (ev: React.FocusEvent<HTMLInputElement>) => {
                if (!warningDialogVariant) {
                    saveNewlyAddedProperty(ev?.currentTarget?.value)
                }
            },
            [saveNewlyAddedProperty, warningDialogVariant]
        )

        const throttledMouseOver = React.useCallback(
            throttle(
                (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                    // This is where the handleMouseOver is attached.
                    const elemId = event?.currentTarget?.id
                    if (!elemId) {
                        return
                    }

                    // This is the element  where the mouse is over.
                    // Since event.target is a BaseSyntheticEvent
                    // It needs to be wrapped to access element properties
                    let target: any = null
                    if (event.target instanceof HTMLDivElement) {
                        target = event.target as HTMLDivElement
                    } else if (event.target instanceof SVGElement) {
                        target = event.target as SVGAElement
                    } else if (event.target instanceof HTMLSpanElement) {
                        target = event.target as HTMLSpanElement
                    } else if (event.target instanceof HTMLParagraphElement) {
                        target = event.target as HTMLParagraphElement
                    }

                    // Excluded as this will cause infinite over and out
                    if (
                        target?.classList.contains('MuiGrid-container') ||
                        target?.classList.contains('notes')
                    ) {
                        return
                    }

                    // Get the closest container and get the itemId
                    const closest = target?.closest(
                        'div.draggableItemContainer'
                    )
                    const classNames: string[] =
                        closest?.className.split(' ') ?? []
                    const elemItemId = classNames.find((each) =>
                        each.match('item')
                    )

                    if (!!closest) {
                        if (!dragStarted && !isDragging && !isOver) {
                            if (
                                elemItemId === elemId &&
                                !disableArticleExpandInFullView
                            ) {
                                setLocalHovered(true)
                            } else {
                                setLocalHovered(false)
                            }
                        }
                    }
                },
                undefined,
                { leading: true, trailing: true }
            ),
            [isDragging, isOver, dragStarted, disableArticleExpandInFullView]
        )

        const handleMouseOver = React.useCallback(
            (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                if (event?.currentTarget?.id) {
                    event?.persist()
                    throttledMouseOver(event)
                }
            },
            [throttledMouseOver]
        )

        const handleMouseLeave = React.useCallback(
            (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                if (draggableItemFocus) return
                if (!dragStarted && !isDragging && !isOver && localHovered) {
                    setLocalHovered(false)
                }
            },
            [isDragging, isOver, dragStarted, localHovered, draggableItemFocus]
        )
        const handleExpandClick = React.useCallback(() => {
            if (disableArticleExpandInFullView) {
                return
            }
            handleIsContentItemReOrdered?.(false)
            setAutoExpandInFullView(true)
            updateExpandState?.(template.contentItem?.id ?? '')
            if (propertyToFocus) {
                resetOnFocusProperty()
            }
        }, [
            handleIsContentItemReOrdered,
            updateExpandState,
            template,
            propertyToFocus,
            disableArticleExpandInFullView,
        ])

        const handleCollapseClick = React.useCallback(() => {
            updateExpandState?.(template.contentItem?.id ?? '')
            if (!template.isSharedProductTypeProperty) {
                persistState?.('propertyOption', [])
                if (getPersistedState?.('projectDialogOpened')) {
                    const serviceWorker = navigator.serviceWorker

                    serviceWorker?.controller?.postMessage({
                        [WORKSPACE_ENABLED_PTP_OPTIONS]: [],
                    })
                }
            }

            if (propertyToFocus) {
                resetOnFocusProperty()
            }
            if (articleIdToFocus) {
                setArticleToFocus(undefined)
            }
        }, [
            updateExpandState,
            template,
            persistState,
            getPersistedState,
            propertyToFocus,
            articleIdToFocus,
        ])
        const disableEditPTContentInFamilyView: boolean = React.useMemo(
            () =>
                !!contentTeamFromStudioPage &&
                !hasTitle &&
                selectedProjectNode?.elementType === 'PRODUCTFAMILY' &&
                !!productTypeInFamilyViewContentType,
            [selectedProjectNode, productTypeInFamilyViewContentType]
        )
        const classes = useStyles({
            localHovered:
                localHovered ||
                isPillsDialogOpen ||
                property.addingState === 'newlyAdded' ||
                (property.hasError ?? false),
            textType: template.contentItem?.textType,
            hasTitle,
            isDraggable,
            localDragStarted,
            hasChild: (template.children?.length || 0) > 0,
            isDragging: isDragging,
            isDraggingOver: isOver,
            isNewlyAddedArticle: hasTitle && property.addingState !== undefined,
            isHidden: template?.isHidden || template?.isDeactivated,
            areHiddenPropertiesVisible: areHiddenPropertiesVisible,
            hasError: property.hasError,
            isExpanded: expanded,
            titleTextHeight: titleTextRef.current?.clientHeight || 0,
            isDroppable,
            isAddedToProject: !!selectedProjectNode?.elementId,
            isToBeHighlighted:
                ((hasTitle &&
                    propertyToFocus &&
                    propertyToFocus ===
                        (property?.contentItem?.elements?.[0] as Partial<
                            TextContentElement
                        >)?.id &&
                    !property.addingState) as boolean) ?? false,
            isSelectedItem: isPillsDialogOpen,
            isOfficeMasterNote: propertyTextType === 'OfficeMasterNote',
            isPremiumPlus,
            disableArticleExpandInFullView,
            productTypeInFamilyViewContentType: productTypeInFamilyViewContentType,
            isProductGroupChild: useOrderInContentItem,
        })

        const commonQueryInputs = React.useMemo(
            () => ({
                partType: typeForFullView
                    ? typeForFullView
                    : WorkspaceTabTitles[tab ?? 0],
                rootArticleContentItemIds: [template?.contentItem?.id],
            }),
            [tab, template, typeForFullView]
        )

        const onClickAddProperty = React.useCallback(() => {
            if (hasTitle && expanded) {
                handleCollapseClick()
            }

            if (
                isEditable &&
                !property.addingState &&
                !property.isHidden &&
                !property?.isDeactivated
            ) {
                if (
                    !contentTeamFromStudioPage &&
                    !areSharedVisible &&
                    (property.isSharedProductTypeProperty ||
                        property.isSharedProperty ||
                        property.isProductType ||
                        property.isProductGroup)
                ) {
                    // User wants to create a new shared property, but shared properties are hidden.
                    // Therefore, show shared properties so the new item isn't filtered out upon save.
                    //
                    // TODO currently, user will need to click Add again (once shared props are visible) to add an item.
                    // We couldn't find an easy way to show shared props AND create a blank property in the right place
                    // upon clicking Add (particularly, when adding an Article).
                    // The add-property logic probably needs to be pulled up into the parent or higher
                    // so that the new element isn't overwritten by the article list when the list gets updated
                    // to include shared props.
                    setAreSharedElementsVisible(true)
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Showing shared properties. Please click Add again if you wish to create a new shared property.',
                            variant: 'info',
                            id: `PropertyItem-CreateNewPropertyInfo`,
                        },
                    })
                } else {
                    handleAddProperty()
                }
            }
        }, [
            isEditable,
            property,
            handleAddProperty,
            handleCollapseClick,
            hasTitle,
            expanded,
            areSharedVisible,
            setAreSharedElementsVisible,
            contentTeamFromStudioPage,
        ])

        const onCancelAddProperty = React.useCallback(() => {
            setWarningDialogVariant('canceladd')
            setIsCancelAddDialogOpen(true)
        }, [])

        const handleGetEditingDetails = React.useCallback(
            (
                currentProperty: NormalizedDocumentTemplateViewItem,
                isForArticle: boolean,
                nodeId: string,
                contentItemParentId: string,
                parentType: string,
                parentKey?: string
            ) => {
                return getPropertyEditingDetails({
                    property: currentProperty,
                    isForArticle,
                    nodeId,
                    contentItemParentId,
                    parentType,
                    parentKey: !isForArticle ? parentKey : undefined,
                })
            },
            [getPropertyEditingDetails]
        )

        const isReadyToPromote = React.useCallback(
            (
                input,
                promoteDemoteRequirements,
                isSustainabilityLevel1NewParagraph
            ) => {
                return (
                    input?.action === 'Promote' &&
                    input.content?.id == 'temporaryTemplateId' &&
                    (property?.id === promoteDemoteRequirements.parent?.id ||
                        input.parent?.parentId === property.id ||
                        isSustainabilityLevel1NewParagraph)
                )
            },
            [property]
        )

        const isReadyToDemote = React.useCallback(
            (input, isSustainabilityLevel1NewParagraph) => {
                return (
                    input?.action === 'Demote' &&
                    input.content?.id === 'temporaryTemplateId' &&
                    (input.parent?.parentId === property.contentItem?.id ||
                        input.parent?.parentId === property.id ||
                        isSustainabilityLevel1NewParagraph)
                )
            },
            [property]
        )

        const updatePromoteDemoteTemporaryParagraph = React.useCallback(
            (
                input,
                promoteDemoteRequirements,
                isSustainabilityLevel1NewParagraph?: boolean
            ) => {
                // Check if temporary paragraph is ready for Promote/Demote
                if (
                    isReadyToPromote(
                        input,
                        promoteDemoteRequirements,
                        isSustainabilityLevel1NewParagraph
                    )
                ) {
                    let contentIdTrigger = input.parent?.itemId
                    updatePromoteDemoteTemporaryParagraphData({
                        content: input.content,
                        action: input.action,
                        contentIdTrigger: contentIdTrigger,
                    })
                    return true
                }

                if (
                    isReadyToDemote(input, isSustainabilityLevel1NewParagraph)
                ) {
                    let contentIdTrigger: string | undefined
                    if (input.parent?.children.length === 0) {
                        contentIdTrigger = input.parent.itemId
                    } else {
                        let countChildren = input?.parent?.children.length
                            ? input?.parent.children.length - 1
                            : 0
                        contentIdTrigger =
                            input?.parent?.children[countChildren]?.itemId
                    }
                    updatePromoteDemoteTemporaryParagraphData({
                        content: input.content,
                        action: input.action,
                        contentIdTrigger: contentIdTrigger,
                    })
                    return true
                }
            },
            [property]
        )

        const ACTIONS = { PROMOTE: 'Promote', DEMOTE: 'Demote' }
        const LEVEL_LIMITS = { MIN: 2, MAX: 6 }

        const checkIfContentLevelisAllowed = React.useCallback(
            (
                action: string,
                contentLevel: number,
                isSustainabilityContent: boolean
            ) => {
                const minimumAllowedLevel = isSustainabilityContent
                    ? 1
                    : LEVEL_LIMITS.MIN
                if (
                    (action === ACTIONS.PROMOTE &&
                        contentLevel <= minimumAllowedLevel) ||
                    (action === ACTIONS.DEMOTE &&
                        contentLevel >= LEVEL_LIMITS.MAX)
                ) {
                    defaultPromoteDemoteSnackBar()
                    return true
                }
            },
            []
        )

        const isSustainabilityContentAllowedToBePromoted = React.useCallback(
            (action, content, isSustainabilityContent) => {
                return (
                    isSustainabilityContent &&
                    action === ACTIONS.PROMOTE &&
                    content?.level === 2 &&
                    !!allContentItems
                )
            },
            [allContentItems]
        )

        const isSustainabilityContentAllowedToBeDemoted = React.useCallback(
            (action, content, isSustainabilityContent) => {
                return (
                    isSustainabilityContent &&
                    action === ACTIONS.DEMOTE &&
                    content?.level === 1 &&
                    !!allContentItems &&
                    contentItemIndex > 0
                )
            },
            [allContentItems, contentItemIndex]
        )

        const handleTemporaryParagraph = React.useCallback(
            (
                action,
                content,
                promoteDemoteRequirements,
                isSustainabilityContent
            ) => {
                if (
                    checkIfContentLevelisAllowed(
                        action,
                        content?.level,
                        isSustainabilityContent
                    )
                ) {
                    return true
                }
                if (action === ACTIONS.PROMOTE) {
                    const isPropertyContainsSelectedContent =
                        parent?.id ===
                            content?.newProperties?.contentItemParentId ||
                        parent?.contentItem?.id ===
                            content?.newProperties?.contentItemParentId
                    // Below condition checks if item is sustainability topic content and it is being promoted to level 1
                    if (
                        isSustainabilityContentAllowedToBePromoted(
                            action,
                            content,
                            isSustainabilityContent
                        )
                    ) {
                        promoteDemoteRequirements.parent = allContentItems?.find(
                            (contentItem) =>
                                contentItem?.itemId === content?.parentId
                        ) as NormalizedDocumentTemplateViewItem
                        return updatePromoteDemoteTemporaryParagraph(
                            promoteDemoteRequirements,
                            promoteDemoteRequirements,
                            true
                        )
                    } else if (isPropertyContainsSelectedContent) {
                        promoteDemoteRequirements.parent = parent
                    }
                } else {
                    let newParentIndex = 0
                    let childrenCount = parent?.children?.length || 0

                    if (
                        (childrenCount <= 1 && !isSustainabilityContent) ||
                        (childrenCount <= 1 &&
                            isSustainabilityContent &&
                            content?.level !== 1)
                    ) {
                        defaultPromoteDemoteSnackBar()
                        return true
                    }

                    for (let i = 0; i < childrenCount; i += 1) {
                        if (parent?.children[i].id === content.id) {
                            newParentIndex = i
                            i = childrenCount
                        }
                    }
                    // Below condition checks if item is sustainability topic content and it is being demoted to level 2
                    if (
                        isSustainabilityContentAllowedToBeDemoted(
                            action,
                            content,
                            isSustainabilityContent
                        )
                    ) {
                        promoteDemoteRequirements.parent = allContentItems?.[
                            contentItemIndex - 1
                        ] as NormalizedDocumentTemplateViewItem

                        cancelAddProperty?.(true)

                        return updatePromoteDemoteTemporaryParagraph(
                            promoteDemoteRequirements,
                            promoteDemoteRequirements,
                            true
                        )
                    } else {
                        promoteDemoteRequirements.parent =
                            parent?.children[newParentIndex - 1]
                    }
                }
            },
            [allContentItems, parent, property]
        )

        const handlePromote = React.useCallback(
            (content, promoteDemoteRequirements, isSustainabilityContent) => {
                const isPropertyContainsSelectedContent = property?.children
                    .find(
                        (itemContent) =>
                            itemContent.id ===
                                content?.documentTemplateViewItem?.itemId ||
                            itemContent.id === content?.parentId
                    )
                    ?.children.find(
                        (itemContent) => itemContent.itemId === content?.itemId
                    )
                // Below condition checks if item is sustainability topic content and it is being promoted to level 1
                if (
                    isSustainabilityContentAllowedToBePromoted(
                        'Promote',
                        content,
                        isSustainabilityContent
                    )
                ) {
                    promoteDemoteRequirements.parent = allContentItems?.find(
                        (contentItem) =>
                            contentItem?.itemId === content?.parentId
                    ) as NormalizedDocumentTemplateViewItem
                } else if (isPropertyContainsSelectedContent) {
                    promoteDemoteRequirements.parent = property
                }
            },
            [property]
        )

        const handleDemote = React.useCallback(
            (content, promoteDemoteRequirements, isSustainabilityContent) => {
                const contentIndex = propertyChildren?.findIndex(
                    (itemContent) => itemContent.id === content?.id
                )
                // Below condition checks if item is sustainability topic content and it is being demoted to level 2
                if (
                    isSustainabilityContentAllowedToBeDemoted(
                        'Demote',
                        content,
                        isSustainabilityContent
                    )
                ) {
                    promoteDemoteRequirements.parent = allContentItems?.[
                        contentItemIndex - 1
                    ] as NormalizedDocumentTemplateViewItem
                } else if (contentIndex >= 0) {
                    promoteDemoteRequirements.parent =
                        propertyChildren[contentIndex - 1]
                }
            },
            [allContentItems, propertyChildren]
        )

        const setPromoteDemoteParentRequirements = React.useCallback(
            (input?: PromoteDemoteRequirements) => {
                const { content } = input || {}
                const isSustainabilityContent = isSustainabilityTopicContent(
                    content
                )
                let promoteDemoteRequirements = Object.assign({}, input)

                if (
                    !!updatePromoteDemoteTemporaryParagraph(
                        input,
                        promoteDemoteRequirements
                    )
                )
                    return
                if (input?.content?.id === 'temporaryTemplateId') {
                    const isTemporaryParagraphHandled = handleTemporaryParagraph(
                        input?.action,
                        content,
                        promoteDemoteRequirements,
                        isSustainabilityContent
                    )
                    if (isTemporaryParagraphHandled) return
                } else {
                    if (input?.action === ACTIONS.PROMOTE) {
                        handlePromote(
                            content,
                            promoteDemoteRequirements,
                            isSustainabilityContent
                        )
                    } else if (input?.action === ACTIONS.DEMOTE) {
                        handleDemote(
                            content,
                            promoteDemoteRequirements,
                            isSustainabilityContent
                        )
                    }
                }

                // Update Promote/Demote Requirements
                setPromoteDemoteRequirements?.(promoteDemoteRequirements)
            },
            [
                property,
                propertyChildren,
                setPromoteDemoteRequirements,
                parent,
                updatePromoteDemoteTemporaryParagraphData,
                contentItemIndex,
                allContentItems,
            ]
        )

        const handleHtmlParagraphPasteFromInput = React.useCallback(
            (pastedHtml: string) => {
                // If user pasted into an existing paragraph, we want the pasted paragraphs to appear after it;
                // otherwise, they're pasting into a "new item" box so it already has the right index.
                const pasteIndex = property?.addingState ? index : index + 1

                // If user is pasting into a "new item" textbox, remove it.
                cancelAddProperty?.()

                const contentItemParentType = selectedTopic
                    ? 'None'
                    : template?.newProperties?.contentItemParentType ===
                      'ContentItem'
                    ? template?.newProperties?.contentItemParentType
                    : parent?.parentType

                // Call the API to process and add the pasted paragraphs
                handleHtmlParagraphPaste?.({
                    html: pastedHtml,
                    index: pasteIndex,
                    contentItemParentId:
                        template.parentId ??
                        template.newProperties?.contentItemParentId ??
                        null,
                    contentItemParentType: contentItemParentType ?? 'None',
                    contentItemOwnerId:
                        template.newProperties?.contentItemOwnerId ?? null,
                    contentItemOwnerType:
                        template.newProperties?.contentItemOwnerType ??
                        'Nugget',
                    productFamilyId: projectProductFamilyId ?? '',
                    productTypePropertyType: isPropertyShared
                        ? 'Shared'
                        : 'Specific',
                    startingLevel: template.level,
                    // get partType from template properties to be able for htmlParagraphPaste handler to work on SMM workspace;
                    // otherwise, it will always set the to default ("General")
                    partType:
                        template.newProperties?.partType?.toUpperCase() ?? '',
                    rootArticleContentItemIds: parent?.articleId
                        ? [parent.articleId]
                        : [],
                    parentPropertyKey: parent?.parentKey ?? null,
                })
            },
            [
                handleHtmlParagraphPaste,
                cancelAddProperty,
                index,
                parent,
                template,
                property,
            ]
        )

        const {
            currentSpecifierNoteId: currentNoteId,
            addNoteSpecifier: onClickAddNoteSpecifier,
        } = useNoteSpecifier()

        // gather input requirements to create a Specifier Note
        const handleNoteSpecifierRequirements = () => {
            if (contentTeamFromStudioPage) {
                setReactiveWorkspaceActions({
                    addNoteSpecifier: onClickAddNoteSpecifier,
                    addNoteSpecifierRequirements: {
                        dtv,
                        property,
                        tab,
                        template,
                        contentItemNodes,
                        parent,
                        selectedTopic,
                        contentTeamFromStudioPage,
                        selectedTemplate: template,
                    },
                })
            }
        }

        // side-effect to set local state's currentSpecifierNoteId, to set focus on SpecifierNote component
        React.useEffect(() => {
            if (currentNoteId && currentNoteId === template?.contentItem?.id) {
                setCurrentSpecifierNoteId(currentNoteId)
                // clean-up specifierNoteID after setting focus
                setReactiveWorkspaceActions({
                    addNoteSpecifierRequirements: {
                        currentSpecifierNoteId: undefined,
                    },
                })
            }
        }, [currentNoteId, template?.contentItem?.id])

        React.useEffect(() => {
            if (isOver && dragStarted && localDragStarted === false) {
                setLocalDragStarted(true)
            }
            if (!isOver && !dragStarted && localDragStarted === true) {
                setLocalDragStarted(false)
                setLocalHovered(false)
            }
        }, [dragStarted, localDragStarted, isOver])

        React.useEffect(() => {
            if (
                isDragging &&
                !localHovered &&
                !disableArticleExpandInFullView
            ) {
                setLocalHovered(true)
            }
        }, [
            localDragStarted,
            localHovered,
            isDragging,
            disableArticleExpandInFullView,
        ])

        React.useEffect(() => {
            if (
                selectedProjectNode?.elementType === 'PRODUCTTYPE' &&
                contentTeamFromStudioPage &&
                template?.containManufacturerParagraph &&
                !expanded &&
                !autoExpandInFullView &&
                toBeLazyLoaded
            ) {
                const currentItemDTV = dtv?.items?.filter(
                    (item) =>
                        item?.documentTemplateViewContents?.[0]
                            ?.documentTemplateViewContentItems?.[0]?.id ===
                        template?.contentItem?.id
                )?.[0]
                if (currentItemDTV) {
                    updateExpandState?.(template?.contentItem?.id!)
                    const divToScroll = document.getElementById(
                        'item' + template?.itemId!
                    )
                    setAutoExpandInFullView(true)
                    setTimeout(function () {
                        divToScroll?.scrollIntoView?.({
                            block: 'start',
                            behavior: 'smooth',
                        })
                    }, 10)
                }
            }
        }, [
            selectedProjectNode,
            template,
            updateExpandState,
            autoExpandInFullView,
            contentTeamFromStudioPage,
            manufacturerArticleIds,
            expanded,
            dtv,
            toBeLazyLoaded,
        ])
        React.useEffect(() => {
            if (
                template !== property &&
                !property?.children?.some(
                    (child) =>
                        child?.addingState &&
                        child?.addingState === 'newlyAdded'
                ) &&
                !isWorkspaceInError &&
                !isAddProhibited
            ) {
                setProperty(template)
                if ((expandStateList?.length ?? 0) > 0) {
                    if (
                        !expandStateList?.includes(
                            property.contentItem?.id ?? ''
                        ) ||
                        (expandStateList?.includes(
                            property.contentItem?.id ?? ''
                        ) &&
                            toBeLazyLoaded)
                    ) {
                        setArticleLoaded(false)
                    }
                } else {
                    setArticleLoaded(false)
                }
            }

            if (
                !isWorkspaceInError &&
                previousAddingState &&
                !isAddProhibited
            ) {
                setProperty(template)
            }

            if (
                template?.children?.length < property?.children?.length &&
                property?.children?.some(
                    (child) =>
                        child?.addingState && child?.addingState === 'saved'
                ) &&
                !isWorkspaceInError
            ) {
                setProperty(property)
            }
        }, [
            template,
            expandStateList,
            toBeLazyLoaded,
            property,
            isWorkspaceInError,
            previousAddingState,
            isAddProhibited,
        ])

        React.useEffect(() => {
            if (ref) {
                if (
                    isWorkspaceInError &&
                    template?.addingState === 'newlyAdded' &&
                    property?.addingState === 'newlyAdded'
                ) {
                    setProperty({
                        ...template,
                        hasError: true,
                    })
                }

                if (
                    property.addingState === 'saved' &&
                    isWorkspaceInError !== undefined &&
                    !isWorkspaceInError &&
                    pendingNewParagraphVariant
                ) {
                    setPendingNewParagraphVariant(undefined)
                }

                if (
                    property.addingState === 'newlyAdded' &&
                    pendingNewParagraphVariant === undefined &&
                    isWorkspaceInError === undefined
                ) {
                    setPendingNewParagraphVariant(property.addingType)
                }
            }
        }, [
            ref,
            isWorkspaceInError,
            property,
            pendingNewParagraphVariant,
            handleChildUpdatesForParents,
            template,
        ])

        React.useEffect(() => {
            if (
                expanded &&
                toBeLazyLoaded &&
                !articleLoaded &&
                !articleLoading
            ) {
                if (selectedProjectNode?.elementType === 'PRODUCTFAMILY') {
                    setArticleLoading(true)
                    const inputQuery = {
                        variables: {
                            id: selectedElementId,
                            elementId: selectedElementId,
                            projectId,
                            ...commonQueryInputs,
                        },
                    }

                    if (selectedProjectNode?.elementId) {
                        getProjectProductFamily(inputQuery)
                    } else {
                        getProductFamily(inputQuery)
                    }
                }
            }
        }, [
            toBeLazyLoaded,
            expanded,
            getProductFamily,
            getProjectProductFamily,
            projectId,
            isEditable,
            selectedElementId,
            articleLoading,
            articleLoaded,
            selectedProjectNode,
            template,
            tab,
            commonQueryInputs,
        ])

        React.useEffect(() => {
            if (expanded) {
                if (toBeLazyLoaded && !articleLoaded) {
                    if (selectedProjectNode?.elementType === 'PRODUCTTYPE') {
                        if (projectProductFamilyId) {
                            if (!selectedProjectNode.elementId) {
                                if (!articleLoading) {
                                    setArticleLoading(true)
                                    getNugget({
                                        variables: {
                                            id:
                                                selectedProjectNode.baseElementId,
                                            projectId,
                                            productFamilyId: projectProductFamilyId,
                                            ...commonQueryInputs,
                                        },
                                    })
                                }
                            } else {
                                if (!articleLoading) {
                                    setArticleLoading(true)
                                    getSelectedNugget({
                                        variables: {
                                            elementId:
                                                selectedProjectNode?.elementId,
                                            projectId,
                                            nodeId: selectedProjectNode?.id,
                                            productFamilyId: projectProductFamilyId,
                                            ...commonQueryInputs,
                                        },
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }, [
            toBeLazyLoaded,
            expanded,
            getNugget,
            selectedProjectNode,
            property,
            tab,
            projectId,
            articleLoading,
            articleLoaded,
            getSelectedNugget,
            commonQueryInputs,
            projectProductFamilyId,
        ])

        React.useEffect(() => {
            if (template.isManufacturers && template.articleId) {
                setManufacturerArticleId?.([template.articleId as string])
            }
        }, [template, commonQueryInputs, setManufacturerArticleId])

        React.useEffect(() => {
            // When editting a paragraph and ENTER key is pressed, saves then a new paragraph container is created underneath the target.
            if (
                isNewAddOnQueue &&
                addQueueSib &&
                addQueueSib?.length > 0 &&
                property?.contentItem?.id === addQueueSib?.[0] &&
                !isAddOngoing &&
                elementIdOnEdit === undefined &&
                !property?.children?.find(
                    (child) => child.id === 'temporaryTemplateId'
                ) &&
                !isAddProhibited
            ) {
                handleAddProperty()
            }
        }, [
            isNewAddOnQueue,
            addQueueSib,
            property,
            isAddOngoing,
            isAddProhibited,
            elementIdOnEdit,
        ])

        //- Add an effect to initiate sync checks:
        React.useEffect(() => {
            if (
                template?.contentItem?.isBestPractice ||
                template?.contentItem?.isGlobalBestPractice
            ) {
                if (processEnv.REACT_APP_EnableSmartProjectCopy === '1') {
                    checkIfBestPracticeIsInSync(null)
                }
            }
        }, [
            template?.contentItem?.id,
            template?.contentItem?.elements,
            template.isHidden,
        ])

        React.useEffect(() => {
            if (
                expanded &&
                articleLoaded &&
                !articleLoading &&
                processEnv.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                checkIfBestPracticeIsInSync(null)
            }
        }, [expanded, articleLoaded, articleLoading])

        // Sets ref values for both DND and intersection observer hook
        const setRefs = React.useCallback(
            (node) => {
                if (ref) {
                    ;(ref as (instance: HTMLDivElement | null) => void)(node)
                }
                if (isVirtualizedItem) {
                    entryRef(node)
                }
            },
            [entryRef, ref, isVirtualizedItem]
        )

        const isSMMSustainabilityContentItem = React.useMemo(
            () =>
                !!(
                    processEnv.REACT_APP_HideGroupSustainabilityContentInSMM !==
                        '1' &&
                    isContentTeamUser &&
                    contentTeamFromStudioPage &&
                    !!property?.topicName &&
                    property?.partType !== 'ContentGroup'
                ),
            [processEnv, property, isContentTeamUser, contentTeamFromStudioPage]
        )

        const sustainabilityTooltipContent = React.useMemo(() => {
            return (
                <div>
                    <Typography variant="h6">{property?.topicName}</Typography>
                    <Typography variant="caption">
                        Topic {property?.topicNumber}
                    </Typography>
                </div>
            )
        }, [property])

        const officeMasterNoteTextContent = React.useMemo(() => {
            return property
                ? property.contentItem?.elements?.map(({ text }) => text)[0]
                : ''
        }, [property])

        const isArticleEditable: boolean = React.useMemo(() => {
            if (
                !property.isHidden ||
                (contentTeamFromStudioPage &&
                    isDraftMode &&
                    !property.isHidden &&
                    (isSystemUser || isOMUser) &&
                    (!isAdmin || isOMUser) &&
                    !disableArticleExpandInFullView)
            ) {
                return true
            }

            return false
        }, [
            contentTeamFromStudioPage,
            property,
            isSystemUser,
            isAdmin,
            disableArticleExpandInFullView,
        ])

        // this function updates workspace content item in real time
        const updateContentItemServiceWorker = React.useCallback(
            (item: Partial<ContentItemModel | DocumentTemplateViewItem>) => {
                startTransition(() => {
                    const updatedContentItems = getUpdatedItemsArray(
                        workspaceContents()?.contentItemNodes ?? [],
                        [item],
                        true,
                        false
                    )

                    contentItemWorker.postMessage({
                        workspaceContentItems: updatedContentItems as ContentItemModel[],
                    })
                    contentItemWorker.onmessage = (
                        event: MessageEvent<ContentItemModel[]>
                    ) => {
                        setWorkspaceContentItems(
                            updatedContentItems.concat(event.data)
                        )
                    }
                })
            },
            [contentItemWorker]
        )

        const [
            getBestPracticeElementData,
            {
                data: bestPracticeElementData,
                loading: bestPracticeElementLoading,
            },
        ] = useLazyQuery<BestPracticeDataResponseExtended>(BestPracticeQuery, {
            fetchPolicy: 'no-cache',
            displayName: 'BestPracticeElement',
            onCompleted: (data) => {
                if (data?.getBestPractice) {
                    checkIfBestPracticeIsOutOfSync(data.getBestPractice)
                }
            },
            onError: (err) => {
                console.log(err)
            },
        })

        // Mutation to check if best practice is out of sync
        const [checkIsBestPracticeOutOfSyncForUI] = useMutation<
            { isBestPracticeOutOfSyncForUI: SyncActionResult },
            { input: Partial<BestPracticeDataExtended> }
        >(IsBestPracticeOutOfSyncForUI, {
            onCompleted: (data) => {
                if (data?.isBestPracticeOutOfSyncForUI) {
                    console.log(data.isBestPracticeOutOfSyncForUI.message)
                    // Add a slight delay before second update to break batch updating
                    setTimeout(() => {
                        setSyncActionType(
                            () => data.isBestPracticeOutOfSyncForUI
                        )
                    }, 100)
                }
            },
        })

        // Mutation to update best practice version
        const [updateBestPracticeVersion] = useMutation<
            any,
            { input: Partial<BestPracticeDataExtended> }
        >(UpdateBestBestPracticeVersion, {
            onCompleted: () => {
                setIsSyncActionLoading(false)
                checkIfBestPracticeIsInSync(null)
            },
        })

        // Check if best practice is out of sync
        const checkIfBestPracticeIsOutOfSync = (
            bestPracticeData: BestPracticeDataExtended
        ) => {
            if (!bestPracticeData) return

            if (processEnv.REACT_APP_EnableSmartProjectCopy === '1') {
                if (isBestPractice || isGlobalBestPractice) {
                    // Start building the map
                    buildHierarchyMap(template, parent)

                    const mutationInput: Partial<BestPracticeDataExtended> = {
                        parentProjectId: projectData?.getProject.id,
                        contentItemId: template?.contentItem?.id,
                        id: bestPracticeData.id,
                        projectElementNodeId: getPersistedState?.(
                            'selectedNodeId'
                        ),
                        elements: bestPracticeData.isUserAddedContentBestPractice
                            ? getRecursiveContentItemElements(template)
                            : getContentItemElements(template), //here sometime the template is not
                        currentVersionId: bestPracticeData.currentVersionId,
                        // Capture current state for sync comparison
                        isHidden: template.isHidden,
                    }
                    checkIsBestPracticeOutOfSyncForUI({
                        variables: {
                            input: mutationInput,
                        },
                    })
                }
            } else {
                console.log('feature flag disabled')
            }
        }

        // Main function to check if best practice is in sync
        const checkIfBestPracticeIsInSync = (
            item: Partial<ContentItemModel> | null,
            checkForId: string | null = null
        ) => {
            if (!checkForId) {
                const isBP = item
                    ? !!item?.isBestPractice
                    : !!template?.contentItem?.isBestPractice
                const isGlobalBP = item
                    ? !!item?.isGlobalBestPractice
                    : !!template?.contentItem?.isGlobalBestPractice
                if (isBP || isGlobalBP) {
                    // If we already have best practice data, use it directly
                    if (bestPracticeElementData?.getBestPractice) {
                        checkIfBestPracticeIsOutOfSync(
                            bestPracticeElementData.getBestPractice
                        )
                    } else {
                        // Otherwise, fetch the best practice data first
                        getBestPracticeElementData({
                            variables: {
                                parentId: template?.contentItem?.id,
                                projectId: projectData?.getProject.id,
                            },
                        })
                    }
                }
            } else {
                // Otherwise, fetch the best practice data first
                getBestPracticeElementData({
                    variables: {
                        parentId: checkForId,
                        projectId: projectData?.getProject.id,
                    },
                })
            }
        }

        // Mutation to update elements from best practice
        const [updateContentFromBestPractice] = useMutation<any, any>(
            UpdateBestPracticeElements(6),
            {
                fetchPolicy: 'no-cache',
                onCompleted: (data) => {
                    if (template?.contentItem?.textType === 'Article') {
                        startTransition(() => {
                            const input = isOMWorkspace
                                ? {
                                      projectId,
                                      parentNodeId:
                                          selectedProjectNode?.elementType ===
                                          'PRODUCTFAMILY'
                                              ? selectedProjectNode?.id!
                                              : selectedProjectNode?.parentNodeId!,
                                  }
                                : {
                                      projectId: projectId,
                                      limit: 1000,
                                      skip: 0,
                                      onlyElementsAssignedToProject: false,
                                      projectElementType: Object.keys(
                                          ProjectElementType
                                      ) as ProjectElementType[],
                                      parentNodeId:
                                          selectedProjectNode?.elementType ===
                                          'PRODUCTFAMILY'
                                              ? selectedProjectNode?.id!
                                              : selectedProjectNode?.parentNodeId!,
                                      isProductFamilyGroupView: false,
                                      isFromDasboardView: true,
                                      isFromAEWorkSpace: !contentTeamFromStudioPage,
                                  }

                            getElementsList({
                                variables: {
                                    input,
                                },
                            })
                        })
                        var isUserAddedContentBestPractice =
                            bestPracticeElementData?.getBestPractice
                                ?.isUserAddedContentBestPractice
                        // Trigger family reload to refresh UI with updated data
                        if (
                            fetchSelectedProductFamilyWithTable &&
                            template?.isUserAdded &&
                            isUserAddedContentBestPractice
                        ) {
                            const timeout = setTimeout(() => {
                                setProperty((past) => ({
                                    ...past,
                                    children: [],
                                }))
                                fetchSelectedProductFamilyWithTable()
                                clearTimeout(timeout)
                            }, 100)
                        }
                    }

                    //deleting the old dtvcontentitem to handle changes in ids
                    startTransition(() => {
                        setWorkspaceContentItems(
                            getUpdatedItemsArray(
                                contentItemNodes ?? [],
                                data?.updateBestPracticeElements
                                    ?.contentItemNodes ?? [],
                                true,
                                false
                            )
                        )
                    })
                    startTransition(() => {
                        handleRetrieveUpdatedDTV(property?.partType)
                        retrieveUpdatedElementChanges()
                    })
                    // run a new getBestPracticeData query with updated data
                    setTimeout(() => {
                        // Refetch best practice data from scratch
                        getBestPracticeElementData({
                            variables: {
                                parentId: template?.contentItem?.id,
                                projectId: projectData?.getProject.id,
                            },
                            // This will then trigger checkIfBestPracticeIsOutOfSync through its onCompleted
                        })

                        setIsSyncActionLoading(false)
                    }, 200) // Delay check to ensure backend sync is complete
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Best Practice Applied And Changes Saved Successfully',
                            variant: 'success',
                            id: `ContentItemElementText-ApplyingBestPracticeSuccess`,
                        },
                    })
                },
                onError: (error) => {
                    setIsSyncActionLoading(false)
                    showGlobalSnackbar({
                        variables: {
                            message: `Error updating from Best Practice: ${error.message}`,
                            variant: 'error',
                            id: 'BestPracticeUpdateError',
                        },
                    })
                },
            }
        )

        // Extract content item elements for mutation input
        const getContentItemElements = (
            template: NormalizedDocumentTemplateViewItem
        ) => {
            if (!template?.contentItem?.elements) {
                return []
            }
            var elements = template.contentItem.elements.map(
                (elementIterator: any) => {
                    const elementData: Partial<Elements> = {
                        id: elementIterator.id,
                        imperialUnits: elementIterator.imperialUnits,
                        metricUnits: elementIterator.metricUnits,
                        optionItemState: elementIterator.optionItemState,
                        text: elementIterator.text,
                        type: elementIterator.type,
                        placeholderText: elementIterator.placeholderText,
                        changeType: elementIterator.changeType,
                        authoredUserId: elementIterator.authoredUserId,
                    }
                    if (elementIterator.elements) {
                        elementData.elements = elementIterator.elements.map(
                            (element: any) => ({
                                id: element.id,
                                imperialUnits: element.imperialUnits,
                                metricUnits: element.metricUnits,
                                optionItemState: element.optionItemState,
                                text: element.text,
                                type: element.type,
                                placeholderText: element.placeholderText,
                                changeType: element.changeType,
                                authoredUserId: element.authoredUserId,
                            })
                        )
                    }
                    return elementData
                }
            )

            if (
                elements &&
                template?.isUserAdded &&
                processEnv.REACT_APP_EnableSmartProjectCopy === '1'
            ) {
                var ref: Partial<AddProjectContentItemInput> = {}

                var result = hierarchyMap.get(template.id)

                if (result) {
                    var properties = getInitialPropertiesForBPConfigAdd(
                        result.template,
                        result?.parent
                    )
                    var text = (elements[0]?.text as string) || 'defaultValue'
                    var {
                        contentItemOwnerType,
                        contentItemParentId,
                        contentItemParentType,
                        partType,
                        textType,
                        //projectProductFamilyId,
                        productTypePropertyType,
                        index,
                        //contentItemOwnerId,
                    } = getPropertyForSettingBestPracticesConfig(
                        text,
                        properties,
                        result.template,
                        result?.parent as NormalizedDocumentTemplateViewItem
                    )
                    ref = {
                        contentItemOwnerType,
                        //contentItemOwnerId,
                        contentItemParentId,
                        contentItemParentType,
                        partType,
                        textType,
                        productTypePropertyType,
                        //projectProductFamilyId,
                        index,
                    }
                    elements[0].userAddedContentBP = ref
                }
            }

            return elements
        }

        const getRecursiveContentItemElements = (
            template: NormalizedDocumentTemplateViewItem,
            reactiveContents = reactiveWorkspaceContents()
        ): any[] => {
            // Get elements from the current template
            const currentElements = getContentItemElements(template)

            // Process each element to prepare for potential children
            const hierarchicalElements: HierarchicalContentElement[] = currentElements.map(
                (element: any) => {
                    return { ...element }
                }
            )

            // If template has children, process each child
            if (template.children && template.children.length > 0) {
                template.children.forEach((childTemplate) => {
                    // Create child-specific editingInput inheriting from parent

                    const childHierarchicalElements = getRecursiveContentItemElements(
                        childTemplate,
                        reactiveContents
                    )

                    // For each parent element, attach child elements based on match
                    hierarchicalElements.forEach((parentElement) => {
                        const matched = template?.contentItem?.elements?.some(
                            (el) => el.id === parentElement.id
                        )

                        if (matched) {
                            if (!parentElement.children)
                                parentElement.children = []

                            childHierarchicalElements.forEach((child) => {
                                parentElement.children!.push({
                                    ...child,
                                })
                            })
                        }
                    })
                })
            }

            return hierarchicalElements
        }

        const hierarchyMap = new Map<
            string,
            {
                template: NormalizedDocumentTemplateViewItem
                parent?: NormalizedDocumentTemplateViewItem
            }
        >()

        const buildHierarchyMap = (
            currentTemplate: NormalizedDocumentTemplateViewItem,
            parentTemplate?: NormalizedDocumentTemplateViewItem
        ) => {
            // Store both the template and its parent
            hierarchyMap.set(currentTemplate.id, {
                template: currentTemplate,
                parent: parentTemplate,
            })

            // Process children recursively
            if (
                currentTemplate.children &&
                currentTemplate.children.length > 0
            ) {
                currentTemplate.children.forEach((childTemplate) => {
                    buildHierarchyMap(childTemplate, currentTemplate)
                })
            }
        }

        const handleSync = (action: BestPracticeSyncAction) => {
            if (!bestPracticeElementData?.getBestPractice) {
                return
            }
            setIsSyncActionLoading(true)
            // Start building the map
            buildHierarchyMap(template, parent)
            var isUserAddedContentBestPractice =
                bestPracticeElementData?.getBestPractice
                    ?.isUserAddedContentBestPractice

            if (action === BestPracticeSyncAction.CreateNewBP) {
                // Update the BP with current UI content
                console.log('Syncing Best Practices')

                const mutationInput: Partial<BestPracticeDataExtended> = {
                    parentProjectId: projectData?.getProject.id,
                    contentItemId: template?.contentItem?.id,
                    id: bestPracticeElementData.getBestPractice.id,
                    projectElementNodeId: getPersistedState?.('selectedNodeId'),
                    elements: isUserAddedContentBestPractice
                        ? getRecursiveContentItemElements(template)
                        : getContentItemElements(template), //here sometime the template is not,
                    isHidden: template?.isHidden,
                }
                updateBestPracticeVersion({
                    variables: {
                        input: mutationInput,
                    },
                })
            } else if (action === BestPracticeSyncAction.SyncFromBP) {
                // Update UI content from BP
                console.log('Applying Best Practices')
                editingInput.elements = []

                const mutationInput: Partial<BestPracticeDataExtended> = {
                    parentProjectId: projectData?.getProject.id,
                    contentItemId: template?.contentItem?.id,
                    id: bestPracticeElementData.getBestPractice.id,
                    projectElementNodeId: getPersistedState?.('selectedNodeId'),
                    elements: isUserAddedContentBestPractice
                        ? getRecursiveContentItemElements(template)
                        : getContentItemElements(template),
                    isHidden: template?.isHidden,
                }

                updateContentFromBestPractice({
                    variables: {
                        input: editingInput,
                        partType: String(
                            editingInput.partType.toLocaleUpperCase()
                        ),
                        projectId: projectData?.getProject.id,
                        rootArticleContentItemIds: [template.articleId || ''],
                        bestPracticeInput: mutationInput,
                        productFamilyId: selectedTopic
                            ? selectedTopic.ppfId
                            : projectProductFamilyId,
                    },
                })
            } else {
                setIsSyncActionLoading(false)
            }
        }

        // Handle click on out-of-sync icon
        const handleOutOfSyncIconClick = (value: string) => {
            if (
                syncActionType.action !== BestPracticeSyncAction.None &&
                syncActionType.action !== BestPracticeSyncAction.Null
            ) {
                handleSync(syncActionType.action)
            } else {
                console.log("'Best Practices are already in sync'")
            }
        }

        const isBPOutOfSync =
            isBestPractice &&
            (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
                syncActionType.action === BestPracticeSyncAction.CreateNewBP)
        const isGBPOutOfSync =
            isGlobalBestPractice &&
            (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
                syncActionType.action === BestPracticeSyncAction.CreateNewBP)

        const handleRightClick = React.useCallback(
            (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                event.preventDefault()
                if (isBestPracticeEditDisabled) return
                if (event.type === 'contextmenu') {
                    handleBPMenuDisplay(event)
                }
            },
            [getBestPracticeElementData, template, projectData]
        )

        const handleBestPractice = React.useCallback(() => {
            getBestPracticeElementData({
                variables: {
                    parentId: template?.contentItem?.id,
                    projectId: projectData?.getProject.id,
                },
            })
        }, [getBestPracticeElementData, template, projectData])

        const handleBPMenuDisplay = React.useCallback(
            (event: React.MouseEvent<HTMLDivElement> | undefined) => {
                setIsBPMenuDisplayed((past) => !past)
                setPopoverAnchor(event?.currentTarget!)
                if (!isBPMenuDisplayed) {
                    handleBestPractice()
                }
            },
            [
                isBPMenuDisplayed,
                handleBestPractice,
                setPopoverAnchor,
                isGlobalBestPractice,
                isBestPractice,
            ]
        )

        const [setAddUpdateBestPractice] = useMutation<
            AddUpdateBestPracticeResponse,
            { input: Partial<BestPracticeDataExtended> }
        >(AddUpdateBestPracticeMutation, {
            onCompleted: (data: AddUpdateBestPracticeResponse) => {
                if (data && data.addUpdateBestPractice) {
                    const item = {
                        ...template?.contentItem,
                        isBestPractice: getIsBP(data.addUpdateBestPractice),
                        isGlobalBestPractice:
                            data.addUpdateBestPractice?.isGlobal ?? false,
                    }
                    updateContentItemServiceWorker?.(item)
                    //check if in sync just after seting a tag
                    //use current item to check not the exising template
                    checkIfBestPracticeIsInSync(item)
                }
            },
        })

        const [setRemoveBestPractice] = useMutation<
            RemoveBestPracticeResponse,
            { input: Partial<BestPracticeDataExtended> }
        >(RemoveBestPracticeMutation, {
            onCompleted: (data: RemoveBestPracticeResponse) => {
                if (data && data.removeBestPractice) {
                    // Refresh the entire tree only if this is a user-added item
                    if (template?.isUserAdded) {
                        const inputQuery = {
                            variables: {
                                id: selectedElementId,
                                elementId: selectedElementId,
                                projectId,
                                ...commonQueryInputs,
                            },
                        }

                        if (selectedProjectNode?.elementId) {
                            getProjectProductFamily(inputQuery)
                        } else {
                            getProductFamily(inputQuery)
                        }
                    } else {
                        const item = {
                            ...template?.contentItem,
                            isBestPractice: false,
                            isGlobalBestPractice: false,
                        }
                        updateContentItemServiceWorker?.(item)
                    }
                }
            },
        })

        const handleBestPracticesMutationData = React.useCallback(
            (mutationData: Partial<BestPracticeDataExtended> | null) => {
                if (mutationData) {
                    const isMutationUpdated = compareBestPracticesMutation(
                        bestPracticeElementData,
                        projectData?.getProject,
                        mutationData
                    )
                    if (isMutationUpdated) {
                        const mutationInput =
                            bestPracticeElementData?.getBestPractice?.id &&
                            bestPracticeElementData?.getBestPractice
                                ?.parentProjectId === projectData?.getProject.id
                                ? {
                                      ...mutationData,
                                      parentProjectId:
                                          projectData?.getProject.id,
                                      contentItemId: template?.contentItem?.id,
                                      id:
                                          bestPracticeElementData
                                              ?.getBestPractice?.id,
                                  }
                                : {
                                      ...mutationData,
                                      parentProjectId:
                                          projectData?.getProject.id,
                                      contentItemId: template?.contentItem?.id,
                                  }

                        mutationInput.projectElementNodeId = getPersistedState?.(
                            'selectedNodeId'
                        )
                        if (
                            processEnv.REACT_APP_EnableSmartProjectCopy === '1'
                        ) {
                            // Start building the map
                            buildHierarchyMap(template, parent)
                            mutationInput.elements = getRecursiveContentItemElements(
                                template
                            )
                            mutationInput.isUserAddedContentBestPractice =
                                template.isUserAdded

                            mutationInput.isHidden = template.isHidden
                        }
                        setAddUpdateBestPractice({
                            variables: {
                                input: mutationInput,
                            },
                        })
                    }
                } else {
                    // removed the other condition to allow user to change the BP setting on different projects
                    if (bestPracticeElementData?.getBestPractice?.id) {
                        setRemoveBestPractice({
                            variables: {
                                input: {
                                    id:
                                        bestPracticeElementData?.getBestPractice
                                            ?.id,
                                    isHidden: template.isHidden,
                                },
                            },
                        })
                    }
                }
                setIsBPMenuDisplayed(false)
            },
            [
                bestPracticeElementData,
                projectData,
                template,
                setAddUpdateBestPractice,
                setRemoveBestPractice,
                setIsBPMenuDisplayed,
                isBPMenuDisplayed,
                getPersistedState,
            ]
        )

        const arrowIconStyle = React.useMemo(() => {
            return disableArticleExpandInFullView
                ? defaultOOFColor
                : ((property?.contentItem?.elements?.[0]
                      ?.changeType as TrackChangeType)?.toLowerCase() ===
                      TrackChangeType.ADDED.toLowerCase() ||
                      (property?.contentItem?.elements?.[0]
                          ?.changeType as TrackChangeType)?.toLowerCase() ===
                          TrackChangeType.DELETED.toLowerCase()) &&
                  isTrackChangeToggleOn
                ? '#FF6060'
                : isPremiumPlus
                ? manufacturerSecondaryColor
                : '#A78966'
        }, [
            isTrackChangeToggleOn,
            isPremiumPlus,
            property,
            disableArticleExpandInFullView,
        ])

        const defaultPromoteDemoteSnackBar = React.useCallback(
            (message?: string) => {
                let errorMessage: ShowGlobalSnackbarMutationProps = {
                    message:
                        message ??
                        'The action could not be completed. Be sure to maintain a consistent outline structure.',
                    variant: 'error',
                    id: 'PromoteDemoteError',
                }
                showGlobalSnackbar({ variables: errorMessage })
            },
            [showGlobalSnackbar]
        )

        // This useEffect triggers promote/demote of temporary paragraphs
        React.useEffect(() => {
            if (temporaryParagraphData?.contentIdTrigger === property.id) {
                // if demoted temporary paragraph triggers at sibling paragraph, manually add a child
                // else use existing add property
                if (
                    temporaryParagraphData.action === 'Demote' &&
                    property.level === temporaryParagraphData.content?.level
                ) {
                    // When promote/demote button is triggered through the action bar, force the value of the element text to the newly added paragraph text
                    if (isPromoteDemoteTriggered) {
                        const elements =
                            temporaryParagraphData?.content?.contentItem
                                ?.elements

                        if (elements && elements[0]) {
                            elements[0].text = newlyAddedParagraphTextValue
                        }
                    }

                    let propertyNewChildren: NormalizedChildDocumentTemplateViewItem[] = []
                    if (temporaryParagraphData.content) {
                        let editedTemporaryParagraphData = JSON.parse(
                            JSON.stringify(temporaryParagraphData.content)
                        )

                        editedTemporaryParagraphData.newProperties.contentItemParentId =
                            property?.id
                        editedTemporaryParagraphData.newProperties.contentItemParentType =
                            property?.type
                        editedTemporaryParagraphData.newProperties.index =
                            temporaryParagraphData.content?.order || 0
                        editedTemporaryParagraphData.level = property.level + 1
                        delete editedTemporaryParagraphData.paragraphOrder
                        propertyNewChildren.push(editedTemporaryParagraphData)
                        handleChildUpdatesForParents(propertyNewChildren)
                    }
                } else {
                    // When promote/demote button is triggered through the action bar, force the value of the element text to the newly added paragraph text
                    if (isPromoteDemoteTriggered) {
                        const elements =
                            temporaryParagraphData?.content?.contentItem
                                ?.elements

                        if (elements && elements[0]) {
                            elements[0].text = newlyAddedParagraphTextValue
                        }
                    }
                    handleAddProperty(
                        temporaryParagraphData?.content?.contentItem
                            ?.elements?.[0].text
                    )
                }
                updatePromoteDemoteTemporaryParagraphData(null)
            }
        }, [temporaryParagraphData]) // useEffect only triggers when temporaryParagraphData gets updated

        /**
         * Determines if the current state is suitable for level of specification automation.
         */
        React.useEffect(() => {
            if (
                selectedProjectNode &&
                template &&
                projectData &&
                familyProjectMetadata
            ) {
                const isInProject = selectedProjectNode?.isInProject || false
                const levelOfSpecificationTag = parseInt(
                    (template?.documentTemplateViewItem as
                        | { levelOfSpecificationTag: string }
                        | undefined)?.levelOfSpecificationTag || ''
                )
                const projectLevelOfSpecification = parseInt(
                    projectData?.getProject.levelOfSpecification || ''
                )

                // For existing project or those with no value in levelOfSpecificationTag always return false
                if (
                    isNaN(projectLevelOfSpecification) ||
                    isNaN(levelOfSpecificationTag) ||
                    levelOfSpecificationTag === 0
                ) {
                    setIsLevelOfSpecificationAutomation(false)
                }

                const result =
                    isInProject &&
                    !isUnderProductGroupArticle &&
                    (familyProjectMetadata?.isTargetLevelOfSpecificationManuallyUpdated
                        ? Number(levelOfSpecificationTag) >
                          Number(familyProjectMetadata?.targetLOD)
                        : Number(levelOfSpecificationTag) >
                          Number(projectLevelOfSpecification))

                setIsLevelOfSpecificationAutomation(result)
            }
        }, [
            selectedProjectNode,
            template?.documentTemplateViewItem,
            projectData?.getProject.levelOfSpecification,
            isUnderProductGroupArticle,
            familyProjectMetadata?.targetLOD,
        ])

        return (
            <>
                <>
                    <DroppableSpot
                        id={`before-${property?.id}`}
                        index={index}
                        containerId={droppableId}
                        disabled={
                            isTable ||
                            !isDroppable ||
                            !isEditable ||
                            !isMovable ||
                            !!isUnderProductGroupArticle
                        }
                        level={property.level}
                        isNestingDisabled={
                            (isTableSibling ||
                                isTable ||
                                hasTitle ||
                                property?.isManufacturers ||
                                property?.isBOD ||
                                (index === 0 &&
                                    (active?.data?.current?.sortable
                                        ?.containerId ?? '') ===
                                        (over?.data?.current?.sortable
                                            ?.containerId ?? '')) ||
                                (over?.data?.current?.sortable?.index < 1 &&
                                    !active?.id?.includes(property?.id))) ??
                            false
                        }
                    ></DroppableSpot>

                    <div
                        data-testid={`item${property.id}`}
                        id={`item${property.id}`}
                        onMouseOver={handleMouseOver}
                        onMouseLeave={handleMouseLeave}
                        onFocusCapture={handleNoteSpecifierRequirements}
                        className={clsx(
                            itemId,
                            parentId,
                            classes.container,
                            'draggableItemContainer',
                            `parentElement-${property?.itemId}`
                        )}
                        style={{
                            ...getItemStyle(showInactivatedElements),
                        }}
                        ref={setRefs}
                    >
                        {!!isSMMSustainabilityContentItem ? (
                            <HoverTooltip
                                text={sustainabilityTooltipContent}
                                placements={
                                    ([
                                        'top-start',
                                    ] as unknown) as TooltipPlacements[]
                                }
                            >
                                <SustainabilityTopicMarker
                                    className={classes.susTopicMarker}
                                />
                            </HoverTooltip>
                        ) : null}
                        {selectedParagraphItemId === template?.itemId && (
                            <div className={classes.elementOverlay}></div>
                        )}
                        {!inView && isVirtualizedItem && !active ? (
                            <LoadingSkeleton height={computedContentHeight} />
                        ) : (
                            <GridContainer
                                direction="column"
                                className={clsx(classes.borderBottom, {
                                    [classes.smmWorkspace]:
                                        processEnv.REACT_APP_HideGroupSustainabilityContentInSMM !==
                                            '1' &&
                                        contentTeamFromStudioPage &&
                                        isContentTeamUser,
                                })}
                            >
                                {losOfflineBoltIcon}
                                {showInactivatedElements && (
                                    <HoverTooltip
                                        text={
                                            isOMDeactivatedTooltipVisible
                                                ? 'Office Master Deactivation'
                                                : ''
                                        }
                                    >
                                        <GridItem
                                            data-testid="templateItem"
                                            className={clsx(
                                                isPillsDialogOpen
                                                    ? classes.contentItemSelected
                                                    : classes.contentItemContainer,
                                                (isDragError && isDraggable) ||
                                                    property.hasError
                                                    ? classes.draggedItemErrorDarkBorder
                                                    : classes.contentItemContainerBorder,
                                                (isDragError && isDraggable) ||
                                                    property.hasError
                                                    ? classes.draggedItemErrorLightBackgroundColor
                                                    : classes.contentItemContainerBackground,
                                                classes.contentItemContainerFamilyGroupProperty
                                            )}
                                        >
                                            {propertyTextType !==
                                                'OfficeMasterNote' && (
                                                <ConditionalHoverTooltip
                                                    isShow={
                                                        property.isUserAdded! &&
                                                        property.level > 1
                                                    }
                                                >
                                                    <div
                                                        className={clsx(
                                                            classes.contentItemDragHandleContainer,
                                                            (isDragError &&
                                                                isDraggable) ||
                                                                property.hasError
                                                                ? classes.draggedItemErrorDarkBackgroundColor
                                                                : classes.dragHandleContainerBackgroundColor,
                                                            isDragging
                                                                ? !isDragError &&
                                                                  isDraggable
                                                                    ? classes.draggingOkayDrop
                                                                    : classes.draggingErrorDrop
                                                                : isDraggable &&
                                                                      classes.normalCursor
                                                        )}
                                                    >
                                                        {isDraggable &&
                                                            !isTable &&
                                                            !isAddLoading &&
                                                            !property?.isHidden && (
                                                                <DragIndicatorIcon
                                                                    htmlColor="#FFFFFF"
                                                                    data-testid="svgDragIcon"
                                                                />
                                                            )}
                                                        {isAddLoading &&
                                                            !isWorkspaceInError && (
                                                                <CircularProgress
                                                                    size={15}
                                                                    className={
                                                                        classes.newPropertyLoading
                                                                    }
                                                                />
                                                            )}
                                                    </div>
                                                </ConditionalHoverTooltip>
                                            )}
                                            <div
                                                className={
                                                    classes.contentItemContent
                                                }
                                                // temporary fix for collapse height on single child
                                                style={{
                                                    height:
                                                        !expanded &&
                                                        hasTitle &&
                                                        template.children
                                                            ?.length === 1 &&
                                                        (template.contentItem?.elements?.[0].text?.trim()
                                                            .length || 0) < 10
                                                            ? 40
                                                            : 'unset',
                                                }}
                                            >
                                                {template?.contentItem?.id !==
                                                    'temporaryContenItemId' &&
                                                    property.contentItem
                                                        ?.textType !==
                                                        'OfficeMasterNote' && (
                                                        <HoveredItemActions
                                                            hovered={
                                                                localHovered
                                                            }
                                                            setDraggableItemFocus={
                                                                setDraggableItemFocus
                                                            }
                                                            hasTitle={hasTitle}
                                                            isDragging={
                                                                !isDragging &&
                                                                isDroppable &&
                                                                localDragStarted &&
                                                                isDraggable
                                                            }
                                                            isPillsDialogOpen={
                                                                isPillsDialogOpen
                                                            }
                                                            onClickPills={
                                                                resetClick
                                                            }
                                                            titleHeight={
                                                                titleTextRef.current &&
                                                                titleTextRef
                                                                    .current
                                                                    ?.clientHeight >=
                                                                    40
                                                                    ? titleTextRef
                                                                          .current
                                                                          ?.clientHeight
                                                                    : 40
                                                            }
                                                            template={property}
                                                            isHidden={
                                                                property?.isHidden ||
                                                                property?.isDeactivated ||
                                                                disableEditPTContentInFamilyView
                                                            }
                                                            onClickAdd={
                                                                isEditable &&
                                                                !property.addingState &&
                                                                !isAddProhibited &&
                                                                !disableEditPTContentInFamilyView
                                                                    ? onClickAddProperty
                                                                    : undefined
                                                            }
                                                            disableArticleExpandInFullView={
                                                                disableArticleExpandInFullView
                                                            }
                                                            hasError={
                                                                property.hasError ??
                                                                (false ||
                                                                    isDragError)
                                                            }
                                                            id={property.id}
                                                            isReviewer={
                                                                isReviewer
                                                            }
                                                            expanded={expanded}
                                                            isPropertyUserAdded={
                                                                property?.isUserAdded
                                                            }
                                                            handleDialogOpen={
                                                                onClickRemove
                                                            }
                                                            deleteFamilyContent={
                                                                deleteFamilyContent
                                                            }
                                                            isRemoveHidden={
                                                                parent
                                                                    ?.contentItem
                                                                    ?.textType ===
                                                                    'Article' &&
                                                                parent?.children
                                                                    ?.length! ===
                                                                    1
                                                            }
                                                            contentTeamFromStudioPage={
                                                                contentTeamFromStudioPage
                                                            }
                                                            isDraftMode={
                                                                isDraftMode
                                                            }
                                                            isElementRemoved={
                                                                isElementRemoved
                                                            }
                                                            isPremiumPlus={
                                                                isPremiumPlus
                                                            }
                                                            productTypeInFamilyViewContentType={
                                                                productTypeInFamilyViewContentType
                                                            }
                                                            isUnderProductGroupArticle={
                                                                isUnderProductGroupArticle
                                                            }
                                                            handleBPMenuDisplay={
                                                                handleBPMenuDisplay
                                                            }
                                                            isBPMenuDisplayed={
                                                                isBPMenuDisplayed
                                                            }
                                                            parent={parent}
                                                            isBestPracticeOutOfSync={
                                                                isBPOutOfSync
                                                            }
                                                            isGlobalBestPracticeOutOfSync={
                                                                isGBPOutOfSync
                                                            }
                                                            isBestPracticeSyncing={
                                                                isSyncActionLoading &&
                                                                isBestPractice
                                                            }
                                                            isGlobalBestPracticeSyncing={
                                                                isSyncActionLoading &&
                                                                isGlobalBestPractice
                                                            }
                                                            isBestPracticeSynced={
                                                                isBestPractice &&
                                                                syncActionType.action ===
                                                                    BestPracticeSyncAction.None
                                                            }
                                                            isGlobalBestPracticeSynced={
                                                                isGlobalBestPractice &&
                                                                syncActionType.action ===
                                                                    BestPracticeSyncAction.None
                                                            }
                                                            handleOutOfSyncIconClick={
                                                                handleOutOfSyncIconClick
                                                            }
                                                            articleLoaded={
                                                                articleLoaded &&
                                                                !articleLoading
                                                            }
                                                        />
                                                    )}
                                                {hasTitle && (
                                                    <GridContainer
                                                        key={`articleName${property.id}`}
                                                        data-testid={`articleName${property.id}`}
                                                        alignContent="space-around"
                                                        alignItems="center"
                                                        className={clsx(
                                                            classes.titleContainer,
                                                            (property.hasError ||
                                                                isDragError) &&
                                                                classes.draggedItemErrorLightBackgroundColor,
                                                            !!property.addingState &&
                                                                parent?.isProduct
                                                                ? classes.titleContainerFlex
                                                                : ''
                                                        )}
                                                        onContextMenu={
                                                            handleRightClick
                                                        }
                                                    >
                                                        <GridContainer
                                                            item
                                                            alignContent="space-around"
                                                            alignItems="center"
                                                            className={
                                                                classes.titleLeftSideItem
                                                            }
                                                        >
                                                            {expanded ? (
                                                                <ExpandMoreIcon
                                                                    data-testid={`collapse${property.id}`}
                                                                    onClick={
                                                                        handleCollapseClick
                                                                    }
                                                                    cursor={
                                                                        'pointer'
                                                                    }
                                                                    htmlColor={
                                                                        arrowIconStyle
                                                                    }
                                                                />
                                                            ) : (
                                                                <ChevronRightIcon
                                                                    data-testid={`expand${property.id}`}
                                                                    onClick={
                                                                        handleExpandClick
                                                                    }
                                                                    cursor={
                                                                        'pointer'
                                                                    }
                                                                    htmlColor={
                                                                        arrowIconStyle
                                                                    }
                                                                />
                                                            )}
                                                        </GridContainer>
                                                        <GridContainer
                                                            data-testid={`articleNameHeader${property.id}`}
                                                            item
                                                            alignContent="space-around"
                                                            alignItems="center"
                                                            className={clsx(
                                                                classes.titleRightSideItem,
                                                                {
                                                                    [classes.titleRightSideItemFlex]:
                                                                        !!property.addingState &&
                                                                        parent?.isProduct,
                                                                }
                                                            )}
                                                            ref={titleTextRef}
                                                        >
                                                            <Article
                                                                isEditable={
                                                                    isArticleEditable
                                                                }
                                                                contentTeamFromStudioPage={
                                                                    contentTeamFromStudioPage
                                                                }
                                                                disableArticleExpandInFullView={
                                                                    disableArticleExpandInFullView
                                                                }
                                                                propertyId={
                                                                    property.id
                                                                }
                                                                articleTitle={
                                                                    articleTitle
                                                                }
                                                                isNewlyAddedArticle={
                                                                    !!property.addingState
                                                                }
                                                                isHidden={
                                                                    property.isHidden ??
                                                                    false
                                                                }
                                                                handleSaveNewArticle={
                                                                    handleSaveNewArticle
                                                                }
                                                                template={
                                                                    property
                                                                }
                                                                getPropertyEditingDetails={
                                                                    handleGetEditingDetails
                                                                }
                                                                elements={
                                                                    property
                                                                        ?.contentItem
                                                                        ?.elements as Partial<
                                                                        TextContentElement
                                                                    >[]
                                                                }
                                                                articleId={
                                                                    expanded &&
                                                                    hasTitle
                                                                        ? (property
                                                                              ?.contentItem
                                                                              ?.id as string)
                                                                        : undefined
                                                                }
                                                                isPremiumPlus={
                                                                    isPremiumPlus
                                                                }
                                                                onCancelAddProperty={
                                                                    onCancelAddProperty
                                                                }
                                                                parent={parent}
                                                                productTypeInFamilyViewContentType={
                                                                    productTypeInFamilyViewContentType
                                                                }
                                                            />
                                                        </GridContainer>
                                                    </GridContainer>
                                                )}

                                                <Collapse
                                                    in={expanded}
                                                    classes={{
                                                        root:
                                                            classes.borderRight,
                                                        wrapper:
                                                            classes.borderRight,
                                                        wrapperInner:
                                                            classes.borderRight,
                                                    }}
                                                >
                                                    {expanded &&
                                                        (articleLoading &&
                                                        property.contentItem
                                                            ?.textType !==
                                                            'Table' ? (
                                                            <div
                                                                className={clsx(
                                                                    'articleLoadState',
                                                                    classes.articleLoadState
                                                                )}
                                                            >
                                                                <Skeleton
                                                                    variant="text"
                                                                    width="50%"
                                                                />
                                                            </div>
                                                        ) : selectedProjectNode?.elementType !==
                                                              'PRODUCTFAMILY' &&
                                                          productTypeInFamilyViewContentType ===
                                                              ProductTypeInFamilyViewContentType.PARENTARTICLEPRODUCTGROUP &&
                                                          !!property?.children
                                                              ?.length &&
                                                          !property?.children?.find(
                                                              (x) =>
                                                                  !x.isForProductType
                                                          ) ? (
                                                            <div
                                                                className={clsx(
                                                                    classes.collapseContainer,
                                                                    property.hasError ||
                                                                        (isDragError &&
                                                                            classes.draggedItemErrorLightBackgroundColor)
                                                                )}
                                                            >
                                                                {property.children
                                                                    .filter(
                                                                        (
                                                                            item
                                                                        ) =>
                                                                            canElementBeShown(
                                                                                item,
                                                                                isTrackChangeToggleOn
                                                                            )
                                                                    )
                                                                    .map(
                                                                        (
                                                                            childDTVItem,
                                                                            index
                                                                        ) => {
                                                                            return (
                                                                                <DraggableItem
                                                                                    template={
                                                                                        childDTVItem
                                                                                    }
                                                                                    isDraggable={
                                                                                        false
                                                                                    }
                                                                                    selectedTopic={
                                                                                        selectedTopic
                                                                                    }
                                                                                    parentType={
                                                                                        'Article'
                                                                                    }
                                                                                    contentItemIndex={
                                                                                        !!isSMMSustainabilityContentItem
                                                                                            ? property?.paragraphOrder ??
                                                                                              0
                                                                                            : index -
                                                                                              property.children
                                                                                                  .slice(
                                                                                                      0,
                                                                                                      index
                                                                                                  )
                                                                                                  .filter(
                                                                                                      (
                                                                                                          x
                                                                                                      ) =>
                                                                                                          !!x.isHidden ||
                                                                                                          (!!x.isDeactivated &&
                                                                                                              !isOMWorkspace)
                                                                                                  )
                                                                                                  .length
                                                                                    }
                                                                                    addProperty={
                                                                                        addProperty
                                                                                    }
                                                                                    parent={
                                                                                        parent
                                                                                    }
                                                                                    isPropertyShared={
                                                                                        isPropertyShared
                                                                                    }
                                                                                    cancelAddProperty={
                                                                                        cancelAddProperty
                                                                                    }
                                                                                    droppableId={
                                                                                        droppableId
                                                                                    }
                                                                                    index={
                                                                                        index
                                                                                    }
                                                                                    isLastChildOfParent={
                                                                                        index ===
                                                                                        (property
                                                                                            .children
                                                                                            ?.length ??
                                                                                            1) -
                                                                                            1
                                                                                    }
                                                                                    contentTeamFromStudioPage={
                                                                                        contentTeamFromStudioPage
                                                                                    }
                                                                                    isDraftMode={
                                                                                        isDraftMode
                                                                                    }
                                                                                    selectedContent={
                                                                                        selectedContent
                                                                                    }
                                                                                    setAreSharedElementsVisible={
                                                                                        setAreSharedElementsVisible
                                                                                    }
                                                                                    projectProductFamilyId={
                                                                                        projectProductFamilyId
                                                                                    }
                                                                                    isAddProhibited={
                                                                                        isAddProhibited
                                                                                    }
                                                                                    isAddOngoing={
                                                                                        isAddOngoing
                                                                                    }
                                                                                    contentItemNodes={
                                                                                        contentItemNodes
                                                                                    }
                                                                                    dtv={
                                                                                        dtv
                                                                                    }
                                                                                    isNewAddOnQueue={
                                                                                        isNewAddOnQueue
                                                                                    }
                                                                                    addQueueSib={
                                                                                        addQueueSib
                                                                                    }
                                                                                    elementIdOnEdit={
                                                                                        elementIdOnEdit
                                                                                    }
                                                                                    tabSelected={
                                                                                        tab
                                                                                    }
                                                                                    productTypeInFamilyViewContentType={
                                                                                        ProductTypeInFamilyViewContentType.CHILDARTICLEPRODUCTTYPE
                                                                                    }
                                                                                    isUnderProductGroupArticle={
                                                                                        true
                                                                                    }
                                                                                    familyAddedProductTypes={
                                                                                        familyAddedProductTypes
                                                                                    }
                                                                                />
                                                                            )
                                                                        }
                                                                    )}
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className={clsx(
                                                                    classes.collapseContainer,
                                                                    property.hasError ||
                                                                        (isDragError &&
                                                                            classes.draggedItemErrorLightBackgroundColor)
                                                                )}
                                                            >
                                                                {showInactivatedElements &&
                                                                    areNotesVisible &&
                                                                    !!notes &&
                                                                    notes?.length >
                                                                        0 && (
                                                                        <GridItem className="notes">
                                                                            <Suspense
                                                                                fallback={
                                                                                    <LoadingSkeleton
                                                                                        height={
                                                                                            computedContentHeight
                                                                                        }
                                                                                    />
                                                                                }
                                                                            >
                                                                                <LazySpecifierNote
                                                                                    key={`specifierNote${property.id}`}
                                                                                    notes={
                                                                                        notes
                                                                                    }
                                                                                    selectedTopic={
                                                                                        selectedTopic
                                                                                    }
                                                                                    template={
                                                                                        template
                                                                                    }
                                                                                    editMode={
                                                                                        isEditable &&
                                                                                        contentTeamFromStudioPage &&
                                                                                        isDraftMode &&
                                                                                        (template?.isSustainabilityContentItem
                                                                                            ? selectedTopic !==
                                                                                              undefined
                                                                                            : true) // disabling editing capabilities on sustainability content notes
                                                                                    }
                                                                                    getParentDetails={
                                                                                        getParentDetails
                                                                                    }
                                                                                    isHidden={
                                                                                        property?.isHidden! ||
                                                                                        property?.isDeactivated ||
                                                                                        (!isDraftMode &&
                                                                                            contentTeamFromStudioPage)!
                                                                                    }
                                                                                    contentTeamFromStudioPage={
                                                                                        contentTeamFromStudioPage
                                                                                    }
                                                                                    parentProductTypeInFamilyViewContentType={
                                                                                        productTypeInFamilyViewContentType
                                                                                    }
                                                                                    isFocused={
                                                                                        isEditable &&
                                                                                        currentSpecifierNoteId ===
                                                                                            template
                                                                                                ?.contentItem
                                                                                                ?.id
                                                                                    }
                                                                                    setCurrentSpecifierNoteId={
                                                                                        setCurrentSpecifierNoteId
                                                                                    }
                                                                                />
                                                                            </Suspense>
                                                                        </GridItem>
                                                                    )}
                                                                {areNotesVisible &&
                                                                    property
                                                                        .contentItem
                                                                        ?.textType ==
                                                                        'OfficeMasterNote' && (
                                                                        <GridItem className="notes">
                                                                            <OfficeMasterNote
                                                                                key={`officeMasterNote${property.id}`}
                                                                                textContent={
                                                                                    officeMasterNoteTextContent ||
                                                                                    ''
                                                                                }
                                                                                isHidden={
                                                                                    property.isHidden ||
                                                                                    property?.isDeactivated ||
                                                                                    false
                                                                                }
                                                                            />
                                                                        </GridItem>
                                                                    )}
                                                                {property
                                                                    .contentItem
                                                                    ?.textType !==
                                                                    'OfficeMasterNote' && (
                                                                    <>
                                                                        {!isBestPracticeViewDisabled &&
                                                                            property
                                                                                .contentItem
                                                                                ?.textType !==
                                                                                'Article' &&
                                                                            // guard condition: don't show BP icon if project imported family, just to not confuse the user if for some reason it was tagged sa BP
                                                                            !isProjectImported &&
                                                                            (isBestPractice ||
                                                                                isGlobalBestPractice) && (
                                                                                <GridItem
                                                                                    className={clsx(
                                                                                        classes.BPIcons,
                                                                                        {
                                                                                            [property.isUserAdded
                                                                                                ? classes.BPIconsHoverOnUserAddedTemplate
                                                                                                : classes.BPIconsHover]:
                                                                                                localHovered &&
                                                                                                (!showInactivatedElements ||
                                                                                                    !areNotesVisible ||
                                                                                                    (!!notes &&
                                                                                                        notes.length ===
                                                                                                            0)),
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    {processEnv.REACT_APP_EnableSmartProjectCopy ===
                                                                                    '1' ? (
                                                                                            <BestPracticesIcons
                                                                                                isBestPractice={
                                                                                                    isBestPractice
                                                                                                }
                                                                                                isGlobalBestPractice={
                                                                                                    isGlobalBestPractice
                                                                                                }
                                                                                                isBestPracticeOutOfSync={
                                                                                                    isBPOutOfSync
                                                                                                }
                                                                                                isGlobalBestPracticeOutOfSync={
                                                                                                    isGBPOutOfSync
                                                                                                }
                                                                                                isBestPracticeSyncing={
                                                                                                    isSyncActionLoading &&
                                                                                                    isBestPractice
                                                                                                }
                                                                                                isGlobalBestPracticeSyncing={
                                                                                                    isSyncActionLoading &&
                                                                                                    isGlobalBestPractice
                                                                                                }
                                                                                                isBestPracticeSynced={
                                                                                                    isBestPractice &&
                                                                                                    syncActionType.action ===
                                                                                                        BestPracticeSyncAction.None
                                                                                                }
                                                                                                isGlobalBestPracticeSynced={
                                                                                                    isGlobalBestPractice &&
                                                                                                    syncActionType.action ===
                                                                                                        BestPracticeSyncAction.None
                                                                                                }
                                                                                                handleOutOfSyncIconClick={
                                                                                                    handleOutOfSyncIconClick
                                                                                                }
                                                                                                syncActionType={syncActionType}
                                                                                            />
                                                                                    ) : (
                                                                                        <BestPracticesIcons
                                                                                            isBestPractice={
                                                                                                isBestPractice
                                                                                            }
                                                                                            isGlobalBestPractice={
                                                                                                isGlobalBestPractice
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </GridItem>
                                                                            )}
                                                                        {canElementBeShown(
                                                                            property,
                                                                            isTrackChangeToggleOn
                                                                        ) && (
                                                                            <Suspense
                                                                                fallback={
                                                                                    <LoadingSkeleton
                                                                                        height={
                                                                                            computedContentHeight
                                                                                        }
                                                                                    />
                                                                                }
                                                                            >
                                                                                <ContentItem
                                                                                    key={`property-${property.id}`}
                                                                                    selectedTopic={
                                                                                        selectedTopic
                                                                                    }
                                                                                    template={
                                                                                        property
                                                                                    }
                                                                                    index={
                                                                                        !!isSMMSustainabilityContentItem
                                                                                            ? property?.paragraphOrder ??
                                                                                              0
                                                                                            : contentItemIndex
                                                                                    }
                                                                                    saveNewlyAddedProperty={
                                                                                        saveNewlyAddedProperty
                                                                                    }
                                                                                    isExpanded={
                                                                                        expanded
                                                                                    }
                                                                                    cancelAddProperty={
                                                                                        onCancelAddProperty
                                                                                    }
                                                                                    isCancelCancelled={
                                                                                        isCancelAddCancelled
                                                                                    }
                                                                                    getSiblings={
                                                                                        getSiblings
                                                                                    }
                                                                                    disableEditPTContentInFamilyView={
                                                                                        disableEditPTContentInFamilyView
                                                                                    }
                                                                                    getParentDetails={
                                                                                        getParentDetails
                                                                                    }
                                                                                    selectedItemIdForPills={
                                                                                        property?.itemId
                                                                                    }
                                                                                    selectedContent={
                                                                                        selectedContent
                                                                                    }
                                                                                    setPromoteDemoteRequirements={
                                                                                        setPromoteDemoteParentRequirements
                                                                                    }
                                                                                    contentTeamFromStudioPage={
                                                                                        contentTeamFromStudioPage
                                                                                    }
                                                                                    isDraftMode={
                                                                                        isDraftMode
                                                                                    }
                                                                                    triggerAddNewProperty={
                                                                                        onClickAddProperty
                                                                                    }
                                                                                    onHtmlParagraphPaste={
                                                                                        handleHtmlParagraphPasteFromInput
                                                                                    }
                                                                                    areHiddenPropertiesVisible={
                                                                                        areHiddenPropertiesVisible
                                                                                    }
                                                                                    parentTemplate={
                                                                                        parent
                                                                                    }
                                                                                    productTypeInFamilyViewContentType={
                                                                                        productTypeInFamilyViewContentType
                                                                                    }
                                                                                    hiddenSiblingsCount={
                                                                                        hiddenSiblingsCount
                                                                                    }
                                                                                    useOrderInContentItem={
                                                                                        useOrderInContentItem
                                                                                    }
                                                                                    productTypeNodeInFamily={
                                                                                        currentProductTypeNodeInFamily
                                                                                    }
                                                                                    deleteFamilyContent={
                                                                                        deleteFamilyContent
                                                                                    }
                                                                                    checkIfBestPracticeIsOutOfSync={
                                                                                        checkIfBestPracticeIsOutOfSync
                                                                                    }
                                                                                />
                                                                            </Suspense>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {!hasTitle &&
                                                                    selectedParagraphItemId ===
                                                                        template?.itemId && (
                                                                        <div
                                                                            className={
                                                                                classes.dialog
                                                                            }
                                                                        >
                                                                            <div>
                                                                                <Radio
                                                                                    size="small"
                                                                                    disableRipple
                                                                                    checked={
                                                                                        localOptionDialogActionState ===
                                                                                        'Reset'
                                                                                    }
                                                                                    onClick={() =>
                                                                                        setLocalOptionDialogActionState(
                                                                                            'Reset'
                                                                                        )
                                                                                    }
                                                                                />
                                                                                Reset
                                                                                All
                                                                                Options
                                                                            </div>

                                                                            <div>
                                                                                <Radio
                                                                                    size="small"
                                                                                    disableRipple
                                                                                    onClick={() =>
                                                                                        setLocalOptionDialogActionState(
                                                                                            'InActive'
                                                                                        )
                                                                                    }
                                                                                    checked={
                                                                                        localOptionDialogActionState ===
                                                                                        'InActive'
                                                                                    }
                                                                                />
                                                                                Deactivate
                                                                                All
                                                                                Options
                                                                            </div>

                                                                            <Button
                                                                                className={clsx(
                                                                                    classes.buttonCancelOkay,
                                                                                    classes.buttonCancel
                                                                                )}
                                                                                onClick={() =>
                                                                                    usePillsChange(
                                                                                        'close'
                                                                                    )
                                                                                }
                                                                            >
                                                                                Cancel
                                                                            </Button>
                                                                            <div
                                                                                className={
                                                                                    classes.saperator
                                                                                }
                                                                            ></div>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    usePillsChange()
                                                                                }
                                                                                style={{
                                                                                    borderTopRightRadius: 4,
                                                                                    borderBottomRightRadius: 4,
                                                                                }}
                                                                                className={
                                                                                    classes.buttonCancelOkay
                                                                                }
                                                                            >
                                                                                Okay
                                                                            </Button>
                                                                        </div>
                                                                    )}
                                                                {(property
                                                                    .children
                                                                    ?.length ||
                                                                    0) > 0 &&
                                                                !isSMMSustainabilityContentItem ? (
                                                                    <DroppableParentChildren
                                                                        child={
                                                                            propertyChildren
                                                                        }
                                                                        type={`${parentType}_${property.id}`}
                                                                        updateParent={
                                                                            handleChildUpdatesForParents
                                                                        }
                                                                        selectedTopic={
                                                                            selectedTopic
                                                                        }
                                                                        parent={
                                                                            property
                                                                        }
                                                                        isArticleShared={
                                                                            isPropertyShared
                                                                        }
                                                                        enableDragErrorColor={
                                                                            isDragError
                                                                        }
                                                                        contentTeamFromStudioPage={
                                                                            contentTeamFromStudioPage
                                                                        }
                                                                        isDraftMode={
                                                                            isDraftMode
                                                                        }
                                                                        selectedContent={
                                                                            selectedContent
                                                                        }
                                                                        setPromoteDemoteRequirements={
                                                                            setPromoteDemoteParentRequirements
                                                                        }
                                                                        setAreSharedElementsVisible={
                                                                            setAreSharedElementsVisible
                                                                        }
                                                                        dtv={
                                                                            dtv
                                                                        }
                                                                        tabSelected={
                                                                            tab
                                                                        }
                                                                        addQueueSib={
                                                                            addQueueSib
                                                                        }
                                                                        contentItemNodes={
                                                                            contentItemNodes
                                                                        }
                                                                        elementIdOnEdit={
                                                                            elementIdOnEdit
                                                                        }
                                                                        isAddOngoing={
                                                                            isAddOngoing
                                                                        }
                                                                        isAddProhibited={
                                                                            isAddProhibited
                                                                        }
                                                                        isNewAddOnQueue={
                                                                            isNewAddOnQueue
                                                                        }
                                                                        projectProductFamilyId={
                                                                            projectProductFamilyId
                                                                        }
                                                                        parentProductTypeInFamilyViewContentType={
                                                                            productTypeInFamilyViewContentType
                                                                        }
                                                                        familyAddedProductTypes={
                                                                            familyAddedProductTypes
                                                                        }
                                                                        productTypeNodeInFamily={
                                                                            currentProductTypeNodeInFamily
                                                                        }
                                                                    />
                                                                ) : (
                                                                    propertyChildren?.map(
                                                                        (
                                                                            child
                                                                        ) => {
                                                                            return (
                                                                                <Suspense
                                                                                    fallback={
                                                                                        <LoadingSkeleton
                                                                                            height={
                                                                                                computedContentHeight
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                >
                                                                                    {showInactivatedElements &&
                                                                                        areNotesVisible &&
                                                                                        child
                                                                                            ?.contentItem
                                                                                            ?.comments
                                                                                            ?.length! >
                                                                                            0 && (
                                                                                            <GridItem className="notes">
                                                                                                <Suspense
                                                                                                    fallback={
                                                                                                        <LoadingSkeleton
                                                                                                            height={
                                                                                                                computedContentHeight
                                                                                                            }
                                                                                                        />
                                                                                                    }
                                                                                                >
                                                                                                    <LazySpecifierNote
                                                                                                        key={`specifierNote${property.id}`}
                                                                                                        notes={
                                                                                                            child
                                                                                                                ?.contentItem
                                                                                                                ?.comments!
                                                                                                        }
                                                                                                        selectedTopic={
                                                                                                            selectedTopic
                                                                                                        }
                                                                                                        template={
                                                                                                            template
                                                                                                        }
                                                                                                        editMode={
                                                                                                            isEditable &&
                                                                                                            contentTeamFromStudioPage &&
                                                                                                            isDraftMode &&
                                                                                                            (template?.isSustainabilityContentItem
                                                                                                                ? selectedTopic !==
                                                                                                                  undefined
                                                                                                                : true) // disabling editing capabilities on sustainability content notes
                                                                                                        }
                                                                                                        getParentDetails={
                                                                                                            getParentDetails
                                                                                                        }
                                                                                                        isHidden={
                                                                                                            property?.isHidden! ||
                                                                                                            property?.isDeactivated! ||
                                                                                                            (!isDraftMode &&
                                                                                                                contentTeamFromStudioPage)!
                                                                                                        }
                                                                                                        contentTeamFromStudioPage={
                                                                                                            contentTeamFromStudioPage
                                                                                                        }
                                                                                                        parentProductTypeInFamilyViewContentType={
                                                                                                            productTypeInFamilyViewContentType
                                                                                                        }
                                                                                                        isFocused={
                                                                                                            isEditable &&
                                                                                                            currentSpecifierNoteId ===
                                                                                                                template
                                                                                                                    ?.contentItem
                                                                                                                    ?.id
                                                                                                        }
                                                                                                        setCurrentSpecifierNoteId={
                                                                                                            setCurrentSpecifierNoteId
                                                                                                        }
                                                                                                    />
                                                                                                </Suspense>
                                                                                            </GridItem>
                                                                                        )}
                                                                                    <ContentItem
                                                                                        key={`property-${child.id}`}
                                                                                        selectedTopic={
                                                                                            selectedTopic
                                                                                        }
                                                                                        template={
                                                                                            child
                                                                                        }
                                                                                        index={
                                                                                            child?.paragraphOrder ??
                                                                                            0
                                                                                        }
                                                                                        saveNewlyAddedProperty={
                                                                                            saveNewlyAddedProperty
                                                                                        }
                                                                                        isExpanded={
                                                                                            expanded
                                                                                        }
                                                                                        cancelAddProperty={
                                                                                            onCancelAddProperty
                                                                                        }
                                                                                        isCancelCancelled={
                                                                                            isCancelAddCancelled
                                                                                        }
                                                                                        getSiblings={
                                                                                            getSiblings
                                                                                        }
                                                                                        disableEditPTContentInFamilyView={
                                                                                            disableEditPTContentInFamilyView
                                                                                        }
                                                                                        getParentDetails={
                                                                                            getParentDetails
                                                                                        }
                                                                                        selectedItemIdForPills={
                                                                                            property?.itemId
                                                                                        }
                                                                                        selectedContent={
                                                                                            selectedContent
                                                                                        }
                                                                                        setPromoteDemoteRequirements={
                                                                                            setPromoteDemoteParentRequirements
                                                                                        }
                                                                                        contentTeamFromStudioPage={
                                                                                            contentTeamFromStudioPage
                                                                                        }
                                                                                        isDraftMode={
                                                                                            isDraftMode
                                                                                        }
                                                                                        triggerAddNewProperty={
                                                                                            onClickAddProperty
                                                                                        }
                                                                                        onHtmlParagraphPaste={
                                                                                            handleHtmlParagraphPasteFromInput
                                                                                        }
                                                                                        areHiddenPropertiesVisible={
                                                                                            areHiddenPropertiesVisible
                                                                                        }
                                                                                        parentTemplate={
                                                                                            parent
                                                                                        }
                                                                                        productTypeInFamilyViewContentType={
                                                                                            productTypeInFamilyViewContentType
                                                                                        }
                                                                                        hiddenSiblingsCount={
                                                                                            hiddenSiblingsCount
                                                                                        }
                                                                                        useOrderInContentItem={
                                                                                            useOrderInContentItem
                                                                                        }
                                                                                        productTypeNodeInFamily={
                                                                                            currentProductTypeNodeInFamily
                                                                                        }
                                                                                        deleteFamilyContent={
                                                                                            deleteFamilyContent
                                                                                        }
                                                                                        checkIfBestPracticeIsOutOfSync={
                                                                                            checkIfBestPracticeIsOutOfSync
                                                                                        }
                                                                                    />
                                                                                </Suspense>
                                                                            )
                                                                        }
                                                                    )
                                                                )}
                                                            </div>
                                                        ))}
                                                </Collapse>
                                            </div>
                                        </GridItem>
                                    </HoverTooltip>
                                )}
                            </GridContainer>
                        )}
                    </div>
                    {isLastChildOfParent &&
                        !active?.id?.includes(property?.id) && (
                            <DroppableSpot
                                id={`after-${property?.id}`}
                                index={index + 1}
                                containerId={droppableId}
                                disabled={!isDroppable || !isMovable}
                                level={property.level}
                                isNestingDisabled={
                                    (isTableSibling ||
                                        isTable ||
                                        hasTitle ||
                                        property?.isManufacturers ||
                                        property?.isBOD ||
                                        (active?.id?.includes(property?.id) &&
                                            (propertyChildren?.length === 0 ||
                                                isLastChildOfParent))) ??
                                    false
                                }
                            />
                        )}
                </>

                {warningDialogVariant !== undefined && (
                    <ConfirmationDialog
                        openDialog={warningDialogVariant !== undefined}
                        handleDialogClose={handleDialogClose}
                        dialogActionCallback={onRemoveFromProject}
                        contentNode={confirmationDialogContent}
                        size="small"
                        dialogHeading={
                            warningDialogVariant === 'canceladd'
                                ? 'Cancel Add'
                                : undefined
                        }
                    />
                )}

                {isBPMenuDisplayed && (
                    <BestPracticesSetting
                        key={`BP-${template?.id}`}
                        popoverAnchor={popoverAnchor}
                        onlyPopover
                        projectDetail={projectData?.getProject}
                        setBestPracticesMutationData={
                            handleBestPracticesMutationData
                        }
                        showBestPracticesIcons={false}
                        data={bestPracticeElementData}
                        loading={bestPracticeElementLoading}
                        isBestPractice={isBestPractice}
                        isGlobalBestPractice={isGlobalBestPractice}
                        isDisabled={isBestPracticeEditDisabled}
                    />
                )}
            </>
        )
    }
)

export default React.memo(DraggableItem)
